export type Lang = 'fr' | 'en';

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

type Dict = Record<string, string>;

// French is the default/source language. Keep existing French wording verbatim;
// English is the alternative. Brand names, DB-sourced data (participant names,
// document contents, extracted fields) and inherently-French domain terms from
// the source data are NOT translated.

const fr: Dict = {
  // ── common ──────────────────────────────────────────────────────────────
  'common.loading': 'Chargement…',
  'common.save': 'Enregistrer',
  'common.saving': 'Enregistrement…',
  'common.cancel': 'Annuler',
  'common.close': 'Fermer',
  'common.add': 'Ajouter',
  'common.back': 'Retour',
  'common.search': 'Rechercher',
  'common.delete': 'Supprimer',
  'common.edit': 'Modifier',
  'common.confirm': 'Confirmer',
  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.all': 'Tous',
  'common.none': 'Aucun',
  'common.retry': 'Réessayer',
  'common.error': 'Une erreur est survenue',
  'common.logout': 'Déconnexion',
  'common.language': 'Langue',
  'common.notMapped': 'Non associé',

  // ── nav / shell ─────────────────────────────────────────────────────────
  'brand.name': 'References',
  'nav.portfolio': 'Portfolio',
  'nav.equipe': 'Équipe',
  'nav.conseillers': 'Conseillers',
  'nav.notifications': 'Notifications',
  'nav.communications': 'Communications',
  'nav.admin': 'Admin',
  'nav.profile': 'Profil',
  'nav.participant': 'Participant',
  'shell.collapse': 'Réduire',
  'shell.myProfile': 'Mon profil',
  'shell.openAdmin': 'Ouvrir le panneau Admin',
  'shell.chooseView': 'Choisir la vue',
  'shell.themeSwitchToDark': 'Passer en mode sombre',
  'shell.themeSwitchToLight': 'Passer en mode clair',
  'shell.user': 'Utilisateur',
  'shell.impersonating': 'Connecté en tant que',
  'shell.impersonatingBy': 'par',
  'shell.stopImpersonating': 'Arrêter',
  'shell.closeTab': "Fermer l'onglet",
  // role labels
  'role.admin': 'Admin',
  'role.respOai': 'Resp. OAI',
  'role.respRi': 'Resp. RI',
  'role.respAll': 'Responsable',
  'role.rm': 'RM',
  // lens labels
  'lens.rm': 'Mes mesures (RM)',
  'lens.rp': 'Mes participants (RP)',
  'lens.oai': 'Domaine OAI',
  'lens.ri': 'Domaine RI',
  'lens.all': 'Tout',
  'lens.auto': 'Automatique',

  // ── profile menu / language switch ──────────────────────────────────────
  'profile.title': 'Profil',
  'profile.menuAria': 'Ouvrir le menu du profil',

  // ── login (app-level props passed to the kit) ───────────────────────────
  'login.subtitle': 'Portail RM',
  'login.oidcButton': 'Se connecter avec Authentik',
  'login.emailPlaceholder': 'votre.nom@aeveil.ch',

  // ── login kit labels ────────────────────────────────────────────────────
  'login.emailLabel': 'E-mail',
  'login.passwordLabel': 'Mot de passe',
  'login.passwordPlaceholder': 'Saisissez votre mot de passe',
  'login.submit': 'Se connecter',
  'login.submitting': 'Connexion…',
  'login.footer.open': 'Inscription ouverte — le premier utilisateur devient administrateur',
  'login.footer.allowlist': 'Seules les adresses e-mail pré-approuvées peuvent se connecter',
  'login.err.emailRequired': 'L’adresse e-mail est requise',
  'login.err.passwordRequired': 'Le mot de passe est requis',
  'login.err.loginFailed': 'Échec de la connexion',
  'login.err.notAuthorized': 'Votre adresse e-mail n’est pas autorisée à accéder à cette application. Contactez un administrateur.',
  'login.err.oidcFailed': 'Échec de l’authentification. Veuillez réessayer.',
  'login.err.noEmail': 'Aucune adresse e-mail n’a été transmise par le fournisseur d’identité.',

  // ── admin — user management kit labels ──────────────────────────────────
  'users.regMode.title': 'Mode d’inscription',
  'users.regMode.open': 'Ouvert',
  'users.regMode.allowlist': 'Liste d’accès',
  'users.regMode.openHelp': 'Toute personne avec une adresse e-mail valide peut s’inscrire. Le premier utilisateur devient administrateur.',
  'users.regMode.allowlistHelp': 'Seules les adresses pré-approuvées peuvent se connecter.',
  'users.allowlist.title': 'Liste des e-mails autorisés',
  'users.allowlist.notEnforced': 'La liste n’est pas appliquée actuellement. Passez en mode Liste d’accès pour restreindre l’inscription.',
  'users.allowlist.helpEnforced': 'Seules ces adresses e-mail peuvent se connecter. Ajoutez les adresses avant la première authentification.',
  'users.allowlist.helpNotEnforced': 'Gérez les adresses en vue du passage en mode Liste d’accès.',
  'users.emailPlaceholder': 'email@exemple.ch',
  'users.namePlaceholder': 'Nom (optionnel)',
  'users.addToAllowlist': 'Ajouter à la liste',
  'users.createAccount': 'Créer le compte',
  'users.errValidEmail': 'Adresse e-mail valide requise',
  'users.col.email': 'E-mail',
  'users.col.status': 'Statut',
  'users.col.added': 'Ajouté le',
  'users.status.registered': 'Inscrit ({name})',
  'users.status.pending': 'En attente',
  'users.impersonate': 'Imiter',
  'users.remove': 'Retirer',
  'users.confirmRemoveEmail': 'Retirer « {email} » de la liste ?',
  'users.mgmt.title': 'Gestion des utilisateurs',
  'users.col.user': 'Utilisateur',
  'users.col.role': 'Rôle',
  'users.col.joined': 'Inscrit le',
  'users.badge.admin': 'Admin',
  'users.badge.user': 'Utilisateur',
  'users.removeAdmin': 'Retirer admin',
  'users.makeAdmin': 'Rendre admin',
  'users.roles': 'Rôles',
  'users.resetPassword': 'Réinitialiser le mot de passe',
  'users.delete': 'Supprimer',
  'users.confirmDelete': 'Supprimer l’utilisateur « {name} » ? Toutes ses données seront également supprimées.',
  'users.newPasswordPlaceholder': 'Nouveau mot de passe (min. 8 caractères)',
  'users.set': 'Définir',
  'users.saveRoles': 'Enregistrer les rôles',
  'users.errPasswordMin': 'Le mot de passe doit contenir au moins 8 caractères',
  'users.passwordResetDone': 'Mot de passe réinitialisé',

  // ── admin page ──────────────────────────────────────────────────────────
  'admin.title': 'Admin',
  'admin.tab.users': 'Utilisateurs',
  'admin.tab.roles': 'Rôles',
  'admin.tab.staffmap': 'Liaison comptes',
  'admin.tab.settings': 'Paramètres',
  // roles tab
  'admin.roles.introBefore': 'Cochez les capacités de chaque utilisateur. Les flags',
  'admin.roles.introAfter': 'sont seedés à partir des mesures actives lors du premier boot, puis ajustables ici.',
  'admin.roles.colName': 'Nom',
  'admin.roles.lastLogin': 'Dernière connexion',
  'admin.roles.never': 'Jamais',
  'admin.cap.admin.label': 'Admin',
  'admin.cap.admin.hint': 'Panneau admin, gestion des comptes',
  'admin.cap.respRmOai.label': 'Resp RM-OAI',
  'admin.cap.respRmOai.hint': 'Responsable des RM côté OAI',
  'admin.cap.respRmRi.label': 'Resp RM-RI',
  'admin.cap.respRmRi.hint': 'Responsable des RM côté RI',
  'admin.cap.respRp.label': 'Resp RP',
  'admin.cap.respRp.hint': 'Responsable des RP (toutes filières)',
  'admin.cap.rmOai.label': 'RM OAI',
  'admin.cap.rmOai.hint': 'Référent Mesure sur des mesures OAI',
  'admin.cap.rmRi.label': 'RM RI',
  'admin.cap.rmRi.hint': 'Référent Mesure sur des mesures RI',
  'admin.cap.rpOai.label': 'RP OAI',
  'admin.cap.rpOai.hint': 'Référent Participant sur des mesures OAI',
  'admin.cap.rpRi.label': 'RP RI',
  'admin.cap.rpRi.hint': 'Référent Participant sur des mesures RI',
  'drilldown.bsp.intakeNote': 'Retour de dossier',
  'drilldown.bsp.intakeSituation': 'Situation à l’entrée',
  'drilldown.bsp.doctor': 'Médecin',
  'admin.identity.roles': 'Rôles & droits',
  'admin.identity.noAccount': 'Pas de compte V2.1 (provisionné à la 1re connexion)',

  // staff-map tab
  'admin.staffmap.intro': 'Associez les comptes de l’application aux fiches du personnel eveil. L’association par e-mail est tentée à la première connexion.',
  'admin.staffmap.userLabel': 'Utilisateur',
  'admin.staffmap.selectUser': 'Sélectionner un utilisateur…',
  'admin.staffmap.staffLabel': 'Personnel',
  'admin.staffmap.selectStaff': 'Sélectionner un membre du personnel…',
  'admin.staffmap.map': 'Associer',
  'admin.staffmap.colStaff': 'Personnel',
  'admin.staffmap.empty': 'Aucune association pour l’instant.',
  // settings tab
  'admin.settings.accentColor': 'Couleur d’accent',
  'admin.settings.logo': 'Logo',
  'admin.settings.logoAlt': 'Logo actuel',
  'admin.settings.noLogo': 'Aucun logo. Téléchargez un fichier PNG.',
  'admin.settings.logoReplace': 'Remplacer',
  'admin.settings.logoUpload': 'Télécharger',
  'admin.settings.aiConfig': 'Configuration IA',
  'admin.settings.aiIntro': 'Configurez le point d’accès API compatible OpenAI pour les fonctions IA (synthèse du journal, brouillon de bilan).',
  'admin.settings.apiUrl': 'URL de l’API',
  'admin.settings.apiUrlDefault': 'Par défaut : http://192.168.10.9:4000/v1 (LiteLLM)',
  'admin.settings.model': 'Modèle',
  'admin.settings.selectModel': 'Sélectionner un modèle…',
  'admin.settings.refresh': 'Actualiser',
  'admin.settings.modelsAvailable': '{n} modèle·s disponible·s',
  'admin.settings.saveAi': 'Enregistrer les paramètres IA',
  'admin.settings.saved': 'Enregistré',
  // ── from A.json ──
  'portfolio.title': 'Portfolio',
  'portfolio.participantCount': '{n} participant·s',
  'portfolio.searchPlaceholder': 'Rechercher un participant, mandant, RM, RP…',
  'portfolio.noResultsFilter': 'Aucun résultat pour ce filtre.',
  'portfolio.noActiveParticipants': 'Aucun participant actif.',
  'portfolio.col.participant': 'Participant',
  'portfolio.col.measureType': 'Type de mesure',
  'portfolio.col.measures': 'Mesures',
  'portfolio.col.totalMonths': 'Mois total',
  'portfolio.col.daysRemaining': 'Jours restants',
  'portfolio.col.hoursPerWeek': 'Heures / semaine',
  'portfolio.col.activityRate': 'Taux d\'activité',
  'portfolio.col.targetRate': 'Taux ciblé',
  'portfolio.col.targetRateTooltip': 'Taux que le mandat AI prescrit pour aujourd\'hui (interpolation linéaire entre le taux de départ et le taux cible). — pour les mesures sans plan extrait.',
  'portfolio.col.presence': 'Présence',
  'portfolio.col.journalAge': 'Âge journal (j)',
  'portfolio.col.journalAgeTooltip': 'Jours depuis le dernier journal',
  'portfolio.col.mandant': 'Mandant',
  'portfolio.col.rm': 'RM',
  'portfolio.col.rmTooltip': 'Référent Mesure',
  'portfolio.col.rp': 'RP',
  'portfolio.col.rpTooltip': 'Référent Participant',
  'portfolio.measuresRI': 'Mesures RI',
  'portfolio.measuresOAI': 'Mesures OAI',
  'portfolio.planProgressive': 'Progression AI : {start}% → {target}% d\'ici {date}',
  'portfolio.planConstant': 'Taux AI constant {pct}%',
  'portfolio.exportExcel': 'Exporter Excel',
  'portfolio.exportExcelTooltip': 'Exporter les lignes affichées (filtres appliqués) en Excel',
  'portfolio.filter.typeAll': 'Type de mesure : tous',
  'portfolio.filter.mandantAll': 'Mandant : tous',
  'portfolio.filter.rmAll': 'RM : tous',
  'portfolio.filter.rpAll': 'RP : tous',
  'portfolio.filter.reset': 'Réinitialiser',
  'portfolio.export.presence30d': 'Présence 30 j (%)',
  'account.tab.account': 'Compte',
  'account.tab.permissions': 'Permissions',
  'account.tab.notifications': 'Notifications',
  'account.pwd.errMinLength': 'Le nouveau mot de passe doit contenir au moins 8 caractères',
  'account.pwd.errMismatch': 'Les mots de passe ne correspondent pas',
  'account.pwd.errFailed': 'Échec du changement de mot de passe',
  'account.field.name': 'Nom',
  'account.field.email': 'Email',
  'account.field.role': 'Rôle',
  'account.role.respAll': 'RESP. TOUS',
  'account.role.respOai': 'RESP. OAI',
  'account.role.respRi': 'RESP. RI',
  'account.role.utilisateur': 'UTILISATEUR',
  'account.field.staffId': 'ID collaborateur',
  'account.field.auth': 'Authentification',
  'account.auth.sso': 'Authentification unique (OIDC)',
  'account.auth.password': 'Mot de passe',
  'account.section.changePassword': 'Changer le mot de passe',
  'account.pwd.current': 'Mot de passe actuel',
  'account.pwd.new': 'Nouveau mot de passe',
  'account.pwd.minLengthHint': 'Au moins 8 caractères',
  'account.pwd.confirm': 'Confirmer le nouveau mot de passe',
  'account.pwd.changed': 'Mot de passe modifié',
  'account.perm.capability': 'Capacité',
  'account.perm.admin': 'Admin',
  'account.perm.respAll': 'Resp. tous',
  'account.perm.respOai': 'Resp. OAI',
  'account.perm.respRi': 'Resp. RI',
  'account.perm.utilisateur': 'Utilisateur',
  'account.perm.description': 'Ce que votre rôle ({role}) permet de faire dans le système.',
  'account.notif.title': 'Préférences de notifications',
  'account.notif.description': 'Choisissez les événements pour lesquels vous souhaitez recevoir une notification.',
  'account.notif.categoryOther': 'Autre',
  'equipe.title': 'Mon équipe',
  'equipe.referentCount': '{n} référent·s',
  'equipe.participantCount': '{n} participant·s',
  'equipe.endingSoonCount': '{n} fin·s proche·s',
  'equipe.description': 'Vue d\'ensemble des référents mesure et de leurs participants actifs. Les barres colorées signalent les situations à surveiller.',
  'equipe.noReferents': 'Aucun référent avec des participants actifs.',
  'equipe.alert.endingSoon': '{n} mesure·s se termine·nt dans les 30 prochains jours',
  'equipe.alert.journalStale': 'Dernier journal en moyenne il y a {n} jours',
  'equipe.metric.avgActivity': 'Activité moyenne',
  'equipe.metric.avgActivityTooltip': 'Heures par semaine en moyenne sur les 4 dernières semaines, tous participants confondus',
  'equipe.metric.avgPresence': 'Présence moyenne',
  'equipe.metric.avgPresenceTooltip': 'Taux de présence moyen de tous les participants sur les 30 derniers jours',
  'equipe.metric.daysNoJournal': 'Jours sans journal',
  'equipe.metric.daysNoJournalTooltip': 'Nombre moyen de jours depuis la dernière entrée de journal, par mesure active',
  'equipe.metric.endingSoon': 'Fins proches',
  'equipe.metric.endingSoonTooltip': 'Nombre de mesures qui se terminent dans les 30 prochains jours',
  'equipe.toggleHide': '▾ Masquer les participants',
  'equipe.toggleShow': '▸ Voir les participants',
  'equipe.col.participant': 'Participant',
  'equipe.col.contract': 'Contrat',
  'equipe.col.mandant': 'Mandant',
  'equipe.col.daysRemaining': 'Jours restants',
  'equipe.col.hoursPerWeek': 'Heures / semaine',
  'conseillers.title': 'Conseillers',
  'conseillers.count': '{n} conseiller·s',
  'conseillers.activeMeasuresCount': '{n} mesure·s active·s',
  'conseillers.totalCaPill': '{amount} CHF/mois',
  'conseillers.totalLifetimePill': '{amount} CHF cumulés',
  'conseillers.description': 'Contacts mandataires ayant des participants en mesure active. Cliquez pour voir le détail.',
  'conseillers.searchPlaceholder': 'Rechercher...',
  'conseillers.filter.allOrganizations': 'Toutes les organisations',
  'conseillers.sort.ca': 'Trier: CA mensuel',
  'conseillers.sort.lifetime': 'Trier: Valeur cumulée',
  'conseillers.sort.active': 'Trier: Mesures actives',
  'conseillers.sort.total': 'Trier: Total mesures',
  'conseillers.sort.name': 'Trier: Nom',
  'conseillers.sort.organization': 'Trier: Organisation',
  'conseillers.noneFound': 'Aucun conseiller trouvé.',
  'conseillers.backToAll': '← Tous les conseillers',
  'conseillers.unknownOrganization': 'Organisation inconnue',
  'conseillers.since': 'Depuis {age} ({date})',
  'conseillers.card.activeCount': '{n} active·s',
  'conseillers.card.endingSoon': '{n} mesure·s se termine·nt dans les 30 prochains jours',
  'conseillers.card.rmPrefix': 'RM : {rm}',
  'conseillers.metric.monthlyRevenue': 'CA mensuel',
  'conseillers.metric.monthlyRevenueTooltip': 'Somme des prix des mesures actives',
  'conseillers.metric.lifetimeValue': 'Valeur cumulée',
  'conseillers.metric.lifetimeValueTooltip': 'Somme prix x mois pour toutes les mesures',
  'conseillers.metric.totalMeasures': 'Total mesures',
  'conseillers.metric.completedTooltip': '{n} terminée·s',
  'conseillers.metric.avgPresence': 'Présence moy.',
  'conseillers.metric.avgPresenceTooltip': 'Taux de présence moyen des participants actifs (30j)',
  'conseillers.metric.relationship': 'Relation',
  'conseillers.metric.firstMandateTooltip': 'Premier mandat: {date}',
  'conseillers.kpi.activity': 'Activité',
  'conseillers.kpi.activityTooltip': 'Heures de présence effective par semaine (moyenne 4 dernières semaines complètes, type « observed », status présent).',
  'conseillers.unit.hoursPerWeek': 'h/sem',
  'conseillers.kpi.activityRate': 'Taux d\'activité',
  'conseillers.kpi.activityRateTooltip': 'Pourcentage d\'occupation : heures effectives par semaine ÷ 40 h (temps plein) × 100.',
  'conseillers.kpi.presence': 'Présence',
  'conseillers.kpi.presenceTooltip': 'Taux de présence des 30 derniers jours : sessions présentes ÷ sessions observées × 100.',
  'conseillers.kpi.measure': 'Mesure',
  'conseillers.kpi.remaining': 'restants',
  'conseillers.unit.chfPerMonth': 'CHF/mois',
  'conseillers.detail.caTooltip': 'Somme des prix mensuels des mesures actives. Cliquez pour voir l\'historique.',
  'conseillers.unit.chf': 'CHF',
  'conseillers.detail.cumulTooltip': 'Somme totale du chiffre d\'affaires mensuel sur toute la durée de la relation. Cliquez pour voir la courbe.',
  'conseillers.detail.measuresLabel': 'Mesures',
  'conseillers.detail.measuresTooltip': 'Nombre de mesures actives. Cliquez pour voir l\'évolution mensuelle.',
  'conseillers.unit.active': 'actives',
  'conseillers.detail.measuresSub': '{total} total · {completed} terminées',
  'conseillers.detail.endingSoonDelta': '{n} fin·s < 30j',
  'conseillers.detail.presenceTooltip': 'Taux de présence moyen de tous les participants. Cliquez pour voir l\'historique mensuel.',
  'conseillers.chart.title.ca': 'CA mensuel — Évolution du chiffre d\'affaires',
  'conseillers.chart.title.cumul': 'Valeur cumulée — Revenu total cumulé dans le temps',
  'conseillers.chart.title.mesures': 'Mesures actives — Nombre de mesures par mois',
  'conseillers.chart.title.presence': 'Présence moyenne — Taux mensuel tous participants',
  'conseillers.chart.activeMeasuresLabel': '{n} mesure·s',
  'conseillers.detail.activeParticipants': 'Participants actifs',
  'conseillers.detail.noActiveParticipants': 'Aucun participant actif.',
  'conseillers.chart.value': 'Valeur',
  'conseillers.chart.rollingAvg': 'Moy. {n} mois',
  'conseillers.relative.months': '{n} mois',
  'conseillers.relative.years': '{n} an·s',
  'conseillers.month.1': 'jan',
  'conseillers.month.2': 'fév',
  'conseillers.month.3': 'mar',
  'conseillers.month.4': 'avr',
  'conseillers.month.5': 'mai',
  'conseillers.month.6': 'jun',
  'conseillers.month.7': 'jul',
  'conseillers.month.8': 'aoû',
  'conseillers.month.9': 'sep',
  'conseillers.month.10': 'oct',
  'conseillers.month.11': 'nov',
  'conseillers.month.12': 'déc',
  // ── from B.json ──
  'drilldown.tab.overview': 'Vue d’ensemble',
  'drilldown.tab.measures': 'Mesures',
  'drilldown.tab.attendance': 'Présence & Activités',
  'drilldown.tab.journal': 'Journal & Rapports',
  'drilldown.tab.timeline': 'Chronologie',
  'drilldown.tab.communications': 'Communications',
  'drilldown.notFound': 'Participant introuvable.',
  'drilldown.absence.short.vacation': 'Vac.',
  'drilldown.absence.short.illness': 'Mal.',
  'drilldown.absence.short.accident': 'Acc.',
  'drilldown.absence.short.measure_suspended': 'Susp.',
  'drilldown.absence.short.default': 'Abs.',
  'drilldown.absence.full.vacation': 'Vacances',
  'drilldown.absence.full.illness': 'Maladie',
  'drilldown.absence.full.accident': 'Accident',
  'drilldown.absence.full.measure_suspended': 'Mesure suspendue',
  'drilldown.absence.full.default': 'Absence',
  'drilldown.measureBanner.contract': 'Contrat {n}',
  'drilldown.measureBanner.daysProgress': '{elapsed} j / {total}',
  'drilldown.measureBanner.none': 'Aucune mesure active.',
  'drilldown.kpi.activity.label': 'Activité effective',
  'drilldown.kpi.activity.info': 'Heures de présence effective la dernière semaine ISO complète (lun–dim). Seules les observations réelles (présences enregistrées, type « observed ») sont comptées. La semaine en cours est exclue car incomplète.\n\nDelta : écart avec la moyenne des 4 dernières semaines actives (semaines avec au moins une présence).',
  'drilldown.kpi.hoursPerWeekSuffix': 'h / semaine',
  'drilldown.kpi.aiTargetTitleProgressive': 'Cible AI aujourd\'hui (interpolée de {start}% à {target}%)',
  'drilldown.kpi.aiTargetTitleConstant': 'Cible AI {target}% constant',
  'drilldown.kpi.aiTargetHours': 'Cible AI : {hours} h ({pct} %)',
  'drilldown.kpi.deltaHours': '{n} h vs moy. 4 sem',
  'drilldown.kpi.rate.label': 'Taux d\'activité',
  'drilldown.kpi.rate.info': 'Pourcentage d’occupation : heures effectives de la dernière semaine complète divisées par 40 h (temps plein).\n\nLe sparkline montre les 4 dernières semaines. Le delta compare la dernière semaine à la moyenne des 4 semaines.',
  'drilldown.kpi.rate.sub': 'sur 40 h temps plein',
  'drilldown.kpi.engagement.value': 'Réalisé/prévu : {pct} %',
  'drilldown.kpi.lastWeekSub': 'Semaine dernière : {hours} h',
  'drilldown.kpi.rate.inline': 'soit {pct} % du temps plein',
  'drilldown.kpi.planVsReal.label': 'Prévu vs Réalisé (4 sem.)',
  'drilldown.kpi.planVsReal.info': 'Ligne pointillée orange = PRÉVU, ligne pleine = RÉALISÉ (% d’une semaine de 40 h ; nombre sous chaque point = réalisé). Dernière semaine terminée. PRÉVU = heures planifiées (inscriptions + non pointé), RÉALISÉ = heures réellement observées ; les deux en heures et en % d’une semaine de 40 h. Bande orange = prévu, barre pleine = réalisé (vert : plan atteint ou dépassé). Chiffre sous chaque barre = réalisé en %.',
  'drilldown.kpi.planVsReal.plannedWord': 'Prévu',
  'drilldown.kpi.planVsReal.hoursSub': 'dernière semaine : prévu {planned_h} h · réalisé {achieved_h} h',
  'drilldown.kpi.planVsReal.noPlan': 'aucun plan sur la période',
  'drilldown.kpi.planVsReal.achievedWord': 'Réalisé',
  'drilldown.kpi.planVsReal.avgSub': 'Moyenne 4 sem. — prévu {planned} % · réalisé {achieved} %',
  'drilldown.kpi.planVsReal.deltaVs': '{n} pts vs prévu',
  'drilldown.kpi.planVsReal.weekTip': 'Prévu {planned} % ({planned_h} h) · Réalisé {achieved} % ({achieved_h} h)',
  'drilldown.kpi.lastWeekRateSub': 'Dernière semaine : {pct} %',
  'drilldown.kpi.engagement.info': 'Heures observées ÷ heures prévues (4 dernières semaines terminées, planned = pointé + non pointé). Contrairement au taux d’activité (semaines actives), les absences pèsent ici.',
  'chart.legend.attended': 'Réalisé (pointé présent)',
  'chart.legend.missedExcused': 'Manqué excusé',
  'chart.legend.missedUnexcused': 'Manqué non excusé',
  'chart.legend.unmarked': 'Non pointé',
  'drilldown.kpi.aiTargetPct': 'Cible AI : {pct} %',
  'drilldown.kpi.deltaPct': '{n} % vs moy. 4 sem',
  'drilldown.kpi.presence.label': 'Présence',
  'drilldown.kpi.presence.info': 'Taux de présence de la dernière semaine complète : sessions présentes ÷ sessions observées × 100.\n\nLe sparkline montre les 4 dernières semaines. Le delta compare la dernière semaine à la moyenne des 4 semaines.',
  'drilldown.kpi.presence.sub': 'dernière semaine',
  'drilldown.kpi.absences.label': 'Absences',
  'drilldown.kpi.absences.info': 'Absences comptabilisées depuis l’entrée à L’Éveil ({months} mois).\n\n• Maladie : jours complets sans aucune présence, excusés avec motif « Maladie ». Droit : 1 jour par mois passé à L’Éveil ({allowed} j autorisés).\n\n• Ateliers manqués : sessions excusées sur des jours où le participant était aussi présent. Tolérance : 3 sessions.\n\n• Non excusées : absences sans justification. Aucune tolérance.',
  'drilldown.kpi.absences.sick': 'Maladie',
  'drilldown.kpi.absences.workshops': 'Ateliers',
  'drilldown.kpi.absences.unexcused': 'Non exc.',
  'drilldown.kpi.absences.remaining': '{remaining} rest. / {total}',
  'drilldown.unit.daysAbbrev': '{n} j',
  'drilldown.chart.title.activity': 'Activité effective — Heures par semaine',
  'drilldown.chart.title.rate': 'Taux d\'activité — % de 40 h temps plein',
  'drilldown.chart.title.presence': 'Présence — Taux hebdomadaire',
  'drilldown.chart.plannedHours': 'Planifié (h)',
  'drilldown.chart.plannedPct': 'Planifié (%)',
  'drilldown.chart.aiTargetProgressive': 'Objectif AI {start}→{target}%',
  'drilldown.chart.aiTargetConstant': 'Objectif AI {target}%',
  'drilldown.appointments.upcoming': 'Prochains rendez-vous',
  'drilldown.appointments.totalCount': '({list} au total)',
  'drilldown.contacts.title': 'Contacts clés',
  'drilldown.contacts.advisor': 'Conseiller',
  'drilldown.objectives.title': 'Objectifs',
  'drilldown.objectives.bspSheet': 'Fiche BSP',
  'drilldown.objectives.reportWithKind': 'Rapport ({kind})',
  'drilldown.objectives.report': 'Rapport',
  'drilldown.objectives.empty': 'Aucun objectif extrait des documents.',
  'drilldown.objectives.activityRate': 'Taux d\'activité',
  'drilldown.objectives.adjusted': 'Ajusté',
  'drilldown.objectives.adjustAction': '✏ Ajuster',
  'drilldown.objectives.adjustActionTitle': 'Ajuster le taux d\'activité de cette mesure',
  'drilldown.objectives.progression': 'Progression',
  'drilldown.objectives.progressionRange': '{start} % → {target} %',
  'drilldown.objectives.progressionConstant': '{target} % (constant)',
  'drilldown.objectives.period': 'Période',
  'drilldown.objectives.source': 'Source',
  'drilldown.objectives.rmAdjustment': 'Ajustement RM',
  'drilldown.objectives.aiMandate': 'Mandat AI',
  'drilldown.objectives.qualitativeObjectives': 'Objectifs qualitatifs',
  'drilldown.objectives.noQuantTarget': 'Sans cible quantifiable',
  'drilldown.objectives.programme': 'Programme',
  'drilldown.objectives.type': 'Type',
  'drilldown.objectives.constant': 'Constant',
  'drilldown.objectives.startPct': 'départ {pct} %',
  'drilldown.objectives.mention': 'Mention',
  'drilldown.objectives.participantReport': 'Rapport participant',
  'drilldown.objectives.participantWords': 'Mots du participant',
  'drilldown.objectives.reportExcerpt': 'Extrait du rapport',
  'drilldown.objectives.viewPdf': 'Voir le PDF',
  'drilldown.objectives.personalObjectives': 'Objectifs personnels',
  'drilldown.objectives.means': 'Moyens',
  'drilldown.objectives.recommendedActions': 'Actions recommandées',
  'drilldown.objectives.motivations': 'Motivations',
  'drilldown.evaluations.title': 'Évaluations',
  'drilldown.personalInfo.title': 'Informations personnelles',
  'drilldown.personalInfo.birth': 'Naissance',
  'drilldown.personalInfo.email': 'Email',
  'drilldown.personalInfo.phone': 'Téléphone',
  'drilldown.personalInfo.address': 'Adresse',
  'drilldown.bsp.title': 'Bilan de situation',
  'drilldown.bsp.date': 'Date',
  'drilldown.bsp.location': 'Lieu',
  'drilldown.bsp.charterSigned': 'Charte signée',
  'drilldown.bsp.referent': 'Référent',
  'drilldown.bsp.formation': 'Formation',
  'drilldown.bsp.health': 'Santé',
  'drilldown.bsp.psychological': 'Psychologique',
  'drilldown.bsp.family': 'Famille',
  'drilldown.bsp.medication': 'Médication',
  'drilldown.bsp.hobbies': 'Intérêts',
  'drilldown.bsp.availability': 'Disponibilité',
  'drilldown.bsp.notes': 'Notes',
  'drilldown.bsp.bspObjectives': 'Objectifs BSP',
  'drilldown.bspDetail.title': 'Fiche premier entretien (BSP)',
  'drilldown.bspDetail.viewSource': 'Voir le document source',
  'drilldown.bspDetail.strengths': 'Forces et ressources',
  'drilldown.bspDetail.limitations': 'Limitations',
  'drilldown.bspDetail.professionalDirection': 'Direction professionnelle',
  'drilldown.bspDetail.alternativePaths': 'Pistes alternatives',
  'drilldown.bspDetail.projectIdeas': 'Idées de projets pro',
  'drilldown.bspDetail.desiredRate': 'Taux d’activité souhaité',
  'drilldown.bspDetail.lastProfession': 'Dernière profession',
  'drilldown.bspDetail.trainings': 'Formation(s)',
  'drilldown.bspDetail.languagesIt': 'Langues / informatique',
  'drilldown.bspDetail.availabilities': 'Disponibilités',
  'drilldown.documents.title': 'Documents',
  'drilldown.documents.attributedCount': '{n} attribué{s}',
  'drilldown.documents.unattributedCount': '{n} non attribué{s}',
  'drilldown.documents.files': 'Fichiers',
  'drilldown.documents.mandateTitle': 'Mandat / décision OAI',
  'drilldown.documents.mandate': 'Mandat',
  'drilldown.documents.intake': 'Accueil',
  'drilldown.documents.sizeKo': '{n} Ko',
  'drilldown.measuresTab.none': 'Aucune mesure trouvée.',
  'drilldown.measuresTab.current': 'En cours',
  'drilldown.measuresTab.contractCostTitle': 'Coût extrait du contrat{n}{type}{months}',
  'drilldown.measuresTab.contractCost': 'Contrat : CHF {amount}',
  'drilldown.measuresTab.catalogPriceTitle': 'Tarif catalogue (subtype_price)',
  'drilldown.measuresTab.catalogPrice': 'Catalogue : CHF {amount}',
  'drilldown.measuresTab.monthsPlanned': '{n} mois prévu',
  'drilldown.measuresTab.article': 'Art. {n}',
  'drilldown.measuresTab.daysRemaining': '{n} j restants',
  'drilldown.measuresTab.mandant': 'Mandant :',
  'drilldown.measuresTab.advisor': 'Conseiller :',
  'drilldown.measuresTab.contract': 'Contrat :',
  'drilldown.measuresTab.presencePct': 'Présence : {pct} %',
  'drilldown.measuresTab.perWeek': '{hours} / sem',
  'drilldown.measuresTab.totalHours': '{hours} total',
  'drilldown.measuresTab.sessionsPerWeek': '{sessions} séances / {weeks} sem',
  'drilldown.measuresTab.vacationDays': '{n} j vacances',
  'drilldown.measuresTab.journalCount': '{n} journal',
  'drilldown.measuresTab.reportCount': '{n} rapports',
  'drilldown.measuresTab.objectives': 'Objectifs :',
  'drilldown.measuresTab.rateRange': 'Taux {start}% → {target}%',
  'drilldown.measuresTab.rateConstant': 'Taux {target}% constant',
  'drilldown.measuresTab.byDate': 'd\'ici {date}',
  'drilldown.measuresTab.sourcePdf': 'source PDF',
  'drilldown.measuresTab.workshops': 'Ateliers :',
  'drilldown.measuresTab.workshopStat': '{activity} : {sessions} s. · {hours}',
  'drilldown.measuresTab.documentsCount': 'Documents ({n}) :',
  'drilldown.dow.mon': 'Lun',
  'drilldown.dow.tue': 'Mar',
  'drilldown.dow.wed': 'Mer',
  'drilldown.dow.thu': 'Jeu',
  'drilldown.dow.fri': 'Ven',
  'drilldown.dow.sat': 'Sam',
  'drilldown.dow.sun': 'Dim',
  'drilldown.attendance.aiTitle': 'Analyse de présence',
  'drilldown.attendance.aiButton': 'Analyser',
  'drilldown.attendance.aiPeriodHint': 'Sans période sélectionnée, l\'analyse couvre les 12 derniers mois de présences.',
  'drilldown.attendance.heatmapTitle': 'Calendrier de présence (12 derniers mois)',
  'drilldown.attendance.appointments': 'Rendez-vous',
  'drilldown.attendance.colDate': 'Date',
  'drilldown.attendance.colType': 'Type',
  'drilldown.attendance.colSchedule': 'Horaire',
  'drilldown.attendance.colStaff': 'Intervenant',
  'drilldown.attendance.colComment': 'Commentaire',
  'drilldown.attendance.viewDetail': 'Voir le détail',
  'drilldown.attendance.plannedWorkshops': 'Ateliers planifiés',
  'drilldown.attendance.currentMeasure': 'mesure actuelle',
  'drilldown.attendance.sessionsPerWeek': '{n} séance{s} / sem',
  'drilldown.attendance.colActivity': 'Activité',
  'drilldown.attendance.colSession': 'Session',
  'drilldown.attendance.colDay': 'Jour',
  'drilldown.attendance.colPeriod': 'Période',
  'drilldown.attendance.ongoing': 'en cours',
  'drilldown.attendance.otherCategory': 'Autres',
  'drilldown.attendance.workshopsAttended': 'Ateliers suivis',
  'drilldown.attendance.presenceStat': 'Présence : {value}',
  'drilldown.attendance.daysCount': '{n} jours',
  'drilldown.attendance.currentMeasureCol': 'Mesure actuelle',
  'drilldown.attendance.allMeasuresCol': 'Toutes mesures',
  'drilldown.attendance.colSessions': 'Séances',
  'drilldown.attendance.colHours': 'Heures',
  'drilldown.attendance.total': 'Total',
  'drilldown.attendance.externalPlacements': 'Stages externes',
  'drilldown.attendance.daysTotal': '{n} jours total',
  'drilldown.attendance.daysLast3mo': '{n} jours (3 derniers mois)',
  'drilldown.attendance.monthlySummary': 'Résumé mensuel',
  'drilldown.attendance.colMonth': 'Mois',
  'drilldown.attendance.colWorkshop': 'Atelier',
  'drilldown.attendance.colAttendances': 'Présences',
  'drilldown.attendance.previousMeasures': '↳ Mesures précédentes',
  'drilldown.attendance.formalAbsences': 'Absences formelles',
  'drilldown.attendance.noAbsences': 'Aucune absence enregistrée.',
  'drilldown.attendance.colStart': 'Début',
  'drilldown.attendance.colEnd': 'Fin',
  'drilldown.journal.period': 'Période',
  'drilldown.journal.from': 'De',
  'drilldown.journal.to': 'À',
  'drilldown.journal.aiPeriodHint': 'Période analysée : {from} → {to}',
  'drilldown.journal.aiPeriodDefault': 'Sans période sélectionnée, la synthèse couvre les 6 derniers mois.',
  'drilldown.journal.clear': '✕ Effacer',
  'drilldown.journal.entriesCount': '{n} / {total} entrées',
  'drilldown.journal.aiSynthesisTitle': 'Synthèse du journal',
  'drilldown.journal.aiSynthesisButton': 'Générer',
  'drilldown.journal.title': 'Journal',
  'drilldown.journal.entryCount': '{n} entrées',
  'drilldown.journal.emptyAll': 'Aucune entrée de journal.',
  'drilldown.journal.emptyPeriod': 'Aucune entrée sur cette période.',
  'drilldown.journal.entryTypeFallback': 'Type {type}',
  'drilldown.journal.attendanceCommentTitle': 'Commentaire général saisi sur une présence (animateur/RP)',
  'drilldown.journal.attendanceBadge': 'Présence',
  'drilldown.journal.byAuthor': 'par {name}',
  'drilldown.journal.reportsTitle': 'Rapports de bilan',
  'drilldown.journal.reportsCount': '{n} rapports',
  'drilldown.journal.noReports': 'Aucun rapport de bilan.',
  'drilldown.journal.charCount': '{n} car.',
  'drilldown.journal.nextSteps': 'Prochaines étapes :',
  'drilldown.journal.noContent': 'Aucun contenu.',
  'drilldown.timeline.icon.attendance': 'Présence',
  'drilldown.timeline.icon.firstWorkshop': 'Nouvel atelier',
  'drilldown.timeline.icon.journal': 'Journal',
  'drilldown.timeline.icon.absence': 'Absence',
  'drilldown.timeline.icon.vacation': 'Vacances',
  'drilldown.timeline.icon.measureStart': 'Début mesure',
  'drilldown.timeline.icon.measureEnd': 'Fin mesure',
  'drilldown.timeline.icon.report': 'Rapport',
  'drilldown.timeline.icon.placement': 'Stage externe',
  'drilldown.timeline.icon.appointment': 'Rendez-vous',
  'drilldown.timeline.icon.appointmentFuture': 'Rendez-vous à venir',
  'drilldown.timeline.weekRange': '{start} – {end} {month} {year}',
  'drilldown.timeline.title': 'Timeline',
  'drilldown.timeline.eventCount': '{n} événements',
  'drilldown.timeline.noEvents': 'Aucun événement.',
  'drilldown.timeline.weekOf': 'Semaine du {range}',
  'drilldown.timeline.firstTime': '★ 1er {list}',
  'drilldown.measuresTab.monthsCount': '{n} mois',
  'drilldown.personalInfo.ageYears': '{n} ans',
  // ── from C.json ──
  'comms.pageTitle': 'Communications',
  'comms.unsentTooltip': 'Brouillons à envoyer',
  'comms.unsentCount': '{n} brouillons',
  'comms.sentTooltip': 'Communications envoyées',
  'comms.sentCount': '{n} envoyées',
  'comms.tabUnsent': 'Brouillons',
  'comms.tabSent': 'Envoyées',
  'comms.tabAll': 'Toutes',
  'comms.searchPlaceholder': 'Rechercher (participant, destinataire, objet…)',
  'comms.errLoad': 'Erreur de chargement',
  'comms.errSave': 'Erreur lors de la sauvegarde',
  'comms.errMarkSent': 'Erreur lors de la mise à jour',
  'comms.errDelete': 'Erreur lors de la suppression',
  'comms.emptyUnsent': 'Aucun brouillon à envoyer.',
  'comms.emptySent': 'Aucune communication envoyée.',
  'comms.emptyAll': 'Aucune communication.',
  'comms.colParticipant': 'Participant',
  'comms.colMeasure': 'Mesure',
  'comms.colType': 'Type',
  'comms.colRecipient': 'Destinataire',
  'comms.colGenerated': 'Généré',
  'comms.colStatus': 'Statut',
  'comms.kindAdjustment': 'Ajustement de taux',
  'comms.generatedBy': 'par {name}',
  'comms.generatedOn': 'Généré {date}',
  'comms.sentBadge': '✓ Envoyé · {date}',
  'comms.sentBadgeLong': '✓ Envoyé le {date}',
  'comms.markedByTooltip': 'Marqué par {name}',
  'comms.draftBadge': '● Brouillon',
  'comms.subjectLabel': 'Objet :',
  'comms.copySubjectPrefix': 'Objet',
  'comms.copied': 'Copié !',
  'comms.copyButton': 'Copier (objet + corps)',
  'comms.openInMail': 'Ouvrir dans mail',
  'comms.openInMailTooltip': 'Ouvrir dans le client mail par défaut',
  'comms.markSent': 'Marquer comme envoyé',
  'comms.openParticipant': 'Ouvrir le participant ↗',
  'comms.confirmMarkSentTitle': 'Marquer comme envoyé ?',
  'comms.confirmDeleteTitle': 'Supprimer ce brouillon ?',
  'comms.confirmMarkSentMsg': 'Cette action est définitive : le statut passera à « Envoyé » avec la date du jour. Vous ne pourrez plus revenir à « Brouillon ».',
  'comms.confirmDeleteMsg': 'Le brouillon sera supprimé définitivement et disparaîtra de la liste.',
  'comms.confirmMarkSentLabel': 'Marquer envoyé',
  'comms.emptyParticipant': 'Aucune communication générée pour ce participant.',
  'comms.emptyParticipantHint': 'Les emails créés lors d\'un ajustement de taux apparaissent ici, prêts à être copiés dans Outlook.',
  'comms.toLabel': 'À :',
  'comms.relatedAdjustment': 'Ajustement #{id}',
  'notifs.minutesAgo': '{n} min',
  'notifs.hoursAgo': 'il y a {n} h',
  'notifs.daysAgo': 'il y a {n} j',
  'notifs.kindJournalStale': 'Journal sans entrée',
  'notifs.reasonJournalAdded': 'Entrée ajoutée',
  'notifs.reasonMesureEnded': 'Mesure terminée',
  'notifs.reasonMutedApproved': 'Mise en sourdine approuvée',
  'notifs.pageTitle': 'Notifications',
  'notifs.activeTooltip': 'Alertes actives',
  'notifs.activeCount': '{n} actives',
  'notifs.archivedTooltip': 'Alertes archivées',
  'notifs.archivedCount': '{n} archivées',
  'notifs.tabOpen': 'Actives',
  'notifs.tabPast': 'Archivées',
  'notifs.tabMuteRequests': 'Demandes de sourdine',
  'notifs.errLoad': 'Erreur de chargement',
  'notifs.errMuteRequest': 'Erreur lors de la demande de sourdine',
  'notifs.errDecision': 'Erreur lors de la décision',
  'notifs.mutePromptTitle': 'Demander une mise en sourdine',
  'notifs.mutePromptBody': 'Cette demande sera envoyée au responsable pour approbation. Si elle est acceptée, cette alerte ne reviendra plus pour la mesure « {measure} » de ',
  'notifs.reasonRequiredLabel': 'Motif (obligatoire)',
  'notifs.reasonPlaceholder': 'Expliquez pourquoi ce participant est exempté de suivi journalier…',
  'notifs.sendRequest': 'Envoyer la demande',
  'notifs.approveTitle': 'Approuver la mise en sourdine ?',
  'notifs.denyTitle': 'Refuser la demande ?',
  'notifs.approveMsg': 'L\'alerte « {kind} » ne reviendra plus pour la mesure « {measure} » de {name}.',
  'notifs.denyMsg': 'La demande de mise en sourdine sera refusée. {name} en sera informé.',
  'notifs.defaultRequester': 'Le demandeur',
  'notifs.approveLabel': 'Approuver',
  'notifs.denyLabel': 'Refuser',
  'notifs.emptyPast': 'Aucune alerte archivée.',
  'notifs.emptyOpen': 'Aucune alerte active. 🎉',
  'notifs.colType': 'Type',
  'notifs.colParticipant': 'Participant',
  'notifs.colMeasure': 'Mesure',
  'notifs.colDetail': 'Détail',
  'notifs.colClosed': 'Clôturé',
  'notifs.colOpenSince': 'Ouverte depuis',
  'notifs.daysSinceNoJournal': '{n} jours sans journal',
  'notifs.lastEntryDate': '(dernière : {date})',
  'notifs.muteTooltip': 'Demander la mise en sourdine pour cette mesure',
  'notifs.muteButton': 'Mute',
  'notifs.emptyMuteRequests': 'Aucune demande de mise en sourdine.',
  'notifs.colStatus': 'Statut',
  'notifs.colRequester': 'Demandeur',
  'notifs.colReason': 'Motif',
  'notifs.colRequested': 'Demandé',
  'notifs.statusPending': 'En attente',
  'notifs.statusApproved': 'Approuvée',
  'notifs.statusDenied': 'Refusée',
  'notifs.noneParenthesized': '(aucun)',
  'notifs.decidedBy': 'par {name}',
  'adjust.historyToggle': 'Historique du plan ({n})',
  'adjust.errLoad': 'Erreur de chargement',
  'adjust.errDelete': 'Erreur lors de la suppression',
  'adjust.badgeMandat': 'MANDAT',
  'adjust.mandatAiLabel': 'Mandat AI',
  'adjust.inEffect': '· en vigueur',
  'adjust.periodLabel': 'Période : {start} – {end}',
  'adjust.effectLine': 'Effet {date} · {created}',
  'adjust.superseded': '· remplacé',
  'adjust.reasonLabel': 'Motif :',
  'adjust.deleteTooltip': 'Supprimer cet ajustement',
  'adjust.confirmDeleteTitle': 'Supprimer cet ajustement ?',
  'adjust.confirmDeleteCurrentMsg': 'L\'ajustement en vigueur sera supprimé. L\'ajustement précédent (s\'il existe) redeviendra actif, sinon le mandat AI initial reprendra le contrôle. Le brouillon d\'email associé sera également supprimé.',
  'adjust.confirmDeleteOldMsg': 'L\'ajustement sera supprimé de l\'historique. Le brouillon d\'email associé sera également supprimé. La chaîne en vigueur n\'est pas modifiée.',
  'adjust.planRange': '{start} % → {target} %',
  'adjust.planUntilSuffix': ' d\'ici {date}',
  'adjust.planConstant': 'Constant à {target} %',
  'adjust.modalTitle': 'Ajuster le taux d\'activité',
  'adjust.genericMeasure': 'mesure',
  'adjust.closeTooltip': 'Fermer (Esc)',
  'adjust.currentPlanLabel': 'Plan actuel',
  'adjust.labelType': 'Type',
  'adjust.typeProgressive': 'Progressif',
  'adjust.typeConstant': 'Constant',
  'adjust.labelRate': 'Taux',
  'adjust.labelDeadline': 'Échéance',
  'adjust.labelSource': 'Source',
  'adjust.sourceAdjustmentRm': 'Ajustement RM',
  'adjust.pdfLink': 'PDF ↗',
  'adjust.objectivesLabel': 'Objectifs :',
  'adjust.noPlanExtracted': 'Aucun plan AI extrait pour cette mesure.',
  'adjust.newPlanLabel': 'Nouveau plan',
  'adjust.labelStartRate': 'Taux début',
  'adjust.labelTargetRate': 'Taux cible',
  'adjust.labelPeriodStart': 'Période début',
  'adjust.labelEffectiveDate': 'Date d\'effet',
  'adjust.mondaySnapHint': '↦ Date d\'effet et début ramenés au lundi de la semaine choisie (les semaines ne sont jamais coupées en deux).',
  'adjust.qualitativeObjectivesLabel': 'Objectifs qualitatifs (un par ligne, optionnel)',
  'adjust.qualitativeObjectivesPlaceholder': 'Ex.: Stabiliser la présence&#10;Atteindre 60 % d\'horaire en juin',
  'adjust.reasonRequiredLabel': 'Motif de l\'ajustement',
  'adjust.reasonPlaceholder': 'Expliquez pourquoi le plan actuel n\'est plus tenable et ce que l\'ajustement vise à atteindre.',
  'adjust.reasonMinLength': 'Minimum 5 caractères.',
  'adjust.oaiNoteLabel': 'Note additionnelle pour le conseiller OAI (optionnel)',
  'adjust.recipientNameLabel': 'Destinataire — nom',
  'adjust.recipientNamePlaceholder': 'Conseiller OAI',
  'adjust.recipientEmailLabel': 'Destinataire — email',
  'adjust.recipientEmailPlaceholder': 'conseiller@oai.ch',
  'adjust.previewToggle': 'Aperçu de l\'email à générer',
  'adjust.subjectLabel': 'Objet :',
  'adjust.submitButton': 'Enregistrer et générer',
  'adjust.errSave': 'Erreur lors de l\'enregistrement',
  // ── from D.json ──
  'chart.noData': 'Aucune donnée',
  'chart.month.jan': 'jan',
  'chart.month.feb': 'fév',
  'chart.month.mar': 'mar',
  'chart.month.apr': 'avr',
  'chart.month.may': 'mai',
  'chart.month.jun': 'jun',
  'chart.month.jul': 'jul',
  'chart.month.aug': 'aoû',
  'chart.month.sep': 'sep',
  'chart.month.oct': 'oct',
  'chart.month.nov': 'nov',
  'chart.month.dec': 'déc',
  'chart.weekday.mon': 'Lun',
  'chart.weekday.tue': 'Mar',
  'chart.weekday.wed': 'Mer',
  'chart.weekday.thu': 'Jeu',
  'chart.weekday.fri': 'Ven',
  'chart.weekdayShort.sun': 'dim',
  'chart.weekdayShort.mon': 'lun',
  'chart.weekdayShort.tue': 'mar',
  'chart.weekdayShort.wed': 'mer',
  'chart.weekdayShort.thu': 'jeu',
  'chart.weekdayShort.fri': 'ven',
  'chart.weekdayShort.sat': 'sam',
  'chart.legend.exceedTitle': 'Actuel supérieur à la cible',
  'chart.legend.exceed': 'Au-dessus',
  'chart.legend.reachedTitle': 'Actuel à la cible (≥ 95 %)',
  'chart.legend.reached': 'Atteint',
  'chart.legend.closeTitle': 'Actuel proche de la cible (80–95 %)',
  'chart.legend.close': 'Proche',
  'chart.legend.belowTitle': 'Actuel sous 80 % de la cible',
  'chart.legend.below': 'En dessous',
  'chart.legend.targetTitle': 'Niveau cible prescrit pour la semaine',
  'chart.legend.target': 'Cible',
  'chart.legend.priorTitle': 'Mesures précédentes du participant (palette atténuée)',
  'chart.legend.prior': 'Mesures précédentes',
  'chart.legend.plannedTitle': 'Heures/présence planifiées (inscriptions actives)',
  'chart.legend.plannedDefault': 'Planifié',
  'chart.legend.noAttendance': 'pas de présence',
  'chart.tooltip.week': 'Semaine {n}',
  'chart.tooltip.priorMeasurePrefix': 'Mesure précédente : ',
  'chart.tooltip.estimated': 'Estimé',
  'chart.tooltip.actual': 'Réalisé',
  'chart.tooltip.planned': 'Planifié',
  'chart.tooltip.plannedUpcoming': 'Planifié (à venir)',
  'chart.tooltip.targetAi': 'Cible AI',
  'chart.tooltip.delta': 'Écart',
  'chart.tooltip.rollingAvg': 'Moy. {n} sem',
  'chart.absence.vacation': 'Vacances',
  'chart.absence.illness': 'Maladie',
  'chart.absence.accident': 'Accident',
  'chart.absence.measureSuspended': 'Mesure suspendue',
  'chart.absence.default': 'Absence',
  'timeline.measure.title': 'Parcours art. 14',
  'timeline.measure.priorMonths': '{n} mois préalables (autre institution)',
  'timeline.measure.budgetLabel': 'Mois {month} / {budget}',
  'timeline.milestone.taux30': '30 %',
  'timeline.milestone.taux50': '50 %',
  'timeline.milestone.tauxMin': 'taux min.',
  'timeline.milestone.etActif': 'ET actif',
  'timeline.milestone.auPlusTard': 'au plus tard',
  'timeline.milestone.stageExt': 'Stage ext.',
  'timeline.milestone.requis': 'requis',
  'timeline.milestone.tooltipBeforeEveil': 'Jalon déjà atteint avant L\'Éveil',
  'timeline.milestone.tooltipPastOk': 'Jalon atteint ✓',
  'timeline.milestone.tooltipPastWarn': 'Jalon non atteint — action requise',
  'timeline.milestone.tooltipApproaching': 'Échéance dans moins de 45 jours',
  'timeline.milestone.tooltipFuture': 'Jalon à venir',
  'timeline.contract.label': 'Contrat {n}',
  'timeline.phase.transition': 'EP → ET',
  'timeline.phase.planned': '(prévu)',
  'timeline.legend.epFull': 'Entraînement progressif (EP)',
  'timeline.legend.etFull': 'Entraînement au travail (ET)',
  'timeline.legend.priorInstitution': 'Préalable autre institution',
  'timeline.legend.today': 'Aujourd\'hui',
  'timeline.ri.title': 'Parcours RI',
  'timeline.ri.tauxActivite': 'Taux d\'activité :',
  'timeline.ri.cycle': 'Cycle {n}',
  'timeline.ri.done': 'Terminé',
  'timeline.ri.contract1Label': 'Contrat 1 — CCC conv.',
  'timeline.ri.contract2Label': 'Contrat 2 — CCC conv.',
  'timeline.ri.contractEndsIn': 'Fin de contrat dans {n} j',
  'timeline.ri.pauseActive': '⏸ Pause : {n} j',
  'timeline.ri.pauseDuration': 'Pause : {n} j',
  'timeline.ri.legendContract1': 'Contrat 1',
  'timeline.ri.legendContract2': 'Contrat 2',
  'ai.generate': 'Générer',
  'ai.from': 'De',
  'ai.to': 'À',
  'ai.generationFailed': 'Échec de la génération',
  'appointment.noComment': 'Aucun commentaire enregistré.',
  'doc.confidence.high': 'attribution sûre',
  'doc.confidence.medium': 'attribution probable',
  'doc.confidence.none': 'non attribué',
  'doc.status.ocrFailed': 'échec OCR',
  'doc.status.ocrProcessing': 'OCR en cours',
  'doc.previewAlt': 'Aperçu page 1 — {name}',
  'doc.page': 'page',
  'doc.pages': 'pages',
  'doc.download': 'Télécharger',
  'doc.summary': 'Résumé',
  'doc.detectedMarkers': 'Marqueurs détectés',
  'doc.ocrText': 'Texte OCR ({n} caractères)',
  'doc.ingestError': 'Erreur d\'ingestion',
};

const en: Dict = {
  // ── common ──────────────────────────────────────────────────────────────
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.add': 'Add',
  'common.back': 'Back',
  'common.search': 'Search',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.confirm': 'Confirm',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.all': 'All',
  'common.none': 'None',
  'common.retry': 'Retry',
  'common.error': 'An error occurred',
  'common.logout': 'Logout',
  'common.language': 'Language',
  'common.notMapped': 'Not mapped',

  // ── nav / shell ─────────────────────────────────────────────────────────
  'brand.name': 'References',
  'nav.portfolio': 'Portfolio',
  'nav.equipe': 'Team',
  'nav.conseillers': 'Advisors',
  'nav.notifications': 'Notifications',
  'nav.communications': 'Communications',
  'nav.admin': 'Admin',
  'nav.profile': 'Profile',
  'nav.participant': 'Participant',
  'shell.collapse': 'Collapse',
  'shell.myProfile': 'My profile',
  'shell.openAdmin': 'Open the Admin panel',
  'shell.chooseView': 'Choose view',
  'shell.themeSwitchToDark': 'Switch to dark mode',
  'shell.themeSwitchToLight': 'Switch to light mode',
  'shell.user': 'User',
  'shell.impersonating': 'Logged in as',
  'shell.impersonatingBy': 'by',
  'shell.stopImpersonating': 'Stop',
  'shell.closeTab': 'Close tab',
  // role labels
  'role.admin': 'Admin',
  'role.respOai': 'Resp. OAI',
  'role.respRi': 'Resp. RI',
  'role.respAll': 'Manager',
  'role.rm': 'RM',
  // lens labels
  'lens.rm': 'My measures (RM)',
  'lens.rp': 'My participants (RP)',
  'lens.oai': 'OAI domain',
  'lens.ri': 'RI domain',
  'lens.all': 'All',
  'lens.auto': 'Automatic',

  // ── profile menu / language switch ──────────────────────────────────────
  'profile.title': 'Profile',
  'profile.menuAria': 'Open profile menu',

  // ── login (app-level props passed to the kit) ───────────────────────────
  'login.subtitle': 'RM Portal',
  'login.oidcButton': 'Log in with Authentik',
  'login.emailPlaceholder': 'your.name@aeveil.ch',

  // ── login kit labels ────────────────────────────────────────────────────
  'login.emailLabel': 'Email',
  'login.passwordLabel': 'Password',
  'login.passwordPlaceholder': 'Enter your password',
  'login.submit': 'Log in',
  'login.submitting': 'Logging in…',
  'login.footer.open': 'Open registration — first user becomes admin',
  'login.footer.allowlist': 'Only pre-approved email addresses can log in',
  'login.err.emailRequired': 'Email is required',
  'login.err.passwordRequired': 'Password is required',
  'login.err.loginFailed': 'Login failed',
  'login.err.notAuthorized': 'Your email is not authorized to access this application. Contact an administrator.',
  'login.err.oidcFailed': 'Authentication failed. Please try again.',
  'login.err.noEmail': 'No email was received from the identity provider.',

  // ── admin — user management kit labels ──────────────────────────────────
  'users.regMode.title': 'Registration mode',
  'users.regMode.open': 'Open',
  'users.regMode.allowlist': 'Allowlist',
  'users.regMode.openHelp': 'Anyone with a valid email can register. The first user becomes admin.',
  'users.regMode.allowlistHelp': 'Only pre-approved emails can log in.',
  'users.allowlist.title': 'Email allowlist',
  'users.allowlist.notEnforced': 'The allowlist is not currently enforced. Switch to Allowlist mode to restrict registration.',
  'users.allowlist.helpEnforced': 'Only these email addresses can log in. Add emails before users authenticate.',
  'users.allowlist.helpNotEnforced': 'Manage emails for when you switch to Allowlist mode.',
  'users.emailPlaceholder': 'email@example.com',
  'users.namePlaceholder': 'Name (optional)',
  'users.addToAllowlist': 'Add to allowlist',
  'users.createAccount': 'Create account',
  'users.errValidEmail': 'Valid email is required',
  'users.col.email': 'Email',
  'users.col.status': 'Status',
  'users.col.added': 'Added',
  'users.status.registered': 'Registered ({name})',
  'users.status.pending': 'Pending',
  'users.impersonate': 'Impersonate',
  'users.remove': 'Remove',
  'users.confirmRemoveEmail': 'Remove "{email}" from the allowlist?',
  'users.mgmt.title': 'User management',
  'users.col.user': 'User',
  'users.col.role': 'Role',
  'users.col.joined': 'Joined',
  'users.badge.admin': 'Admin',
  'users.badge.user': 'User',
  'users.removeAdmin': 'Remove admin',
  'users.makeAdmin': 'Make admin',
  'users.roles': 'Roles',
  'users.resetPassword': 'Reset password',
  'users.delete': 'Delete',
  'users.confirmDelete': 'Delete user "{name}"? This will also delete all their data.',
  'users.newPasswordPlaceholder': 'New password (min 8 chars)',
  'users.set': 'Set',
  'users.saveRoles': 'Save roles',
  'users.errPasswordMin': 'Password must be at least 8 characters',
  'users.passwordResetDone': 'Password reset successfully',

  // ── admin page ──────────────────────────────────────────────────────────
  'admin.title': 'Admin',
  'admin.tab.users': 'Users',
  'admin.tab.roles': 'Roles',
  'admin.tab.staffmap': 'Account linking',
  'admin.tab.settings': 'Settings',
  // roles tab
  'admin.roles.introBefore': 'Check each user’s capabilities. The',
  'admin.roles.introAfter': 'flags are seeded from active measures on first boot, then adjustable here.',
  'admin.roles.colName': 'Name',
  'admin.roles.lastLogin': 'Last login',
  'admin.roles.never': 'Never',
  'admin.cap.admin.label': 'Admin',
  'admin.cap.admin.hint': 'Admin panel, account management',
  'admin.cap.respRmOai.label': 'Resp RM-OAI',
  'admin.cap.respRmOai.hint': 'Manager of RMs on the OAI side',
  'admin.cap.respRmRi.label': 'Resp RM-RI',
  'admin.cap.respRmRi.hint': 'Manager of RMs on the RI side',
  'admin.cap.respRp.label': 'Resp RP',
  'admin.cap.respRp.hint': 'Manager of RPs (all streams)',
  'admin.cap.rmOai.label': 'RM OAI',
  'admin.cap.rmOai.hint': 'Measure referent on OAI measures',
  'admin.cap.rmRi.label': 'RM RI',
  'admin.cap.rmRi.hint': 'Measure referent on RI measures',
  'admin.cap.rpOai.label': 'RP OAI',
  'admin.cap.rpOai.hint': 'Participant referent on OAI measures',
  'admin.cap.rpRi.label': 'RP RI',
  'admin.cap.rpRi.hint': 'Participant referent on RI measures',
  'drilldown.bsp.intakeNote': 'Dossier feedback',
  'drilldown.bsp.intakeSituation': 'Situation at entry',
  'drilldown.bsp.doctor': 'Doctor',
  'admin.identity.roles': 'Roles & grants',
  'admin.identity.noAccount': 'No V2.1 account (provisioned at first login)',

  // staff-map tab
  'admin.staffmap.intro': 'Map app users to eveil staff records. Auto-mapping by email is attempted on first login.',
  'admin.staffmap.userLabel': 'User',
  'admin.staffmap.selectUser': 'Select user…',
  'admin.staffmap.staffLabel': 'Staff',
  'admin.staffmap.selectStaff': 'Select staff…',
  'admin.staffmap.map': 'Map',
  'admin.staffmap.colStaff': 'Staff',
  'admin.staffmap.empty': 'No mappings yet.',
  // settings tab
  'admin.settings.accentColor': 'Accent color',
  'admin.settings.logo': 'Logo',
  'admin.settings.logoAlt': 'Current logo',
  'admin.settings.noLogo': 'No logo. Upload a PNG file.',
  'admin.settings.logoReplace': 'Replace',
  'admin.settings.logoUpload': 'Upload',
  'admin.settings.aiConfig': 'AI configuration',
  'admin.settings.aiIntro': 'Configure the OpenAI-compatible API endpoint for AI features (journal synthesis, bilan draft).',
  'admin.settings.apiUrl': 'API URL',
  'admin.settings.apiUrlDefault': 'Default: http://192.168.10.9:4000/v1 (LiteLLM)',
  'admin.settings.model': 'Model',
  'admin.settings.selectModel': 'Select model…',
  'admin.settings.refresh': 'Refresh',
  'admin.settings.modelsAvailable': '{n} models available',
  'admin.settings.saveAi': 'Save AI settings',
  'admin.settings.saved': 'Saved',
  // ── from A.json ──
  'portfolio.title': 'Portfolio',
  'portfolio.participantCount': '{n} participants',
  'portfolio.searchPlaceholder': 'Search for a participant, client, RM, RP…',
  'portfolio.noResultsFilter': 'No results for this filter.',
  'portfolio.noActiveParticipants': 'No active participants.',
  'portfolio.col.participant': 'Participant',
  'portfolio.col.measureType': 'Measure type',
  'portfolio.col.measures': 'Measures',
  'portfolio.col.totalMonths': 'Total months',
  'portfolio.col.daysRemaining': 'Days remaining',
  'portfolio.col.hoursPerWeek': 'Hours / week',
  'portfolio.col.activityRate': 'Activity rate',
  'portfolio.col.targetRate': 'Target rate',
  'portfolio.col.targetRateTooltip': 'Rate prescribed by the AI mandate for today (linear interpolation between the starting rate and the target rate) — for measures without an extracted plan.',
  'portfolio.col.presence': 'Presence',
  'portfolio.col.journalAge': 'Journal age (d)',
  'portfolio.col.journalAgeTooltip': 'Days since the last journal entry',
  'portfolio.col.mandant': 'Client',
  'portfolio.col.rm': 'RM',
  'portfolio.col.rmTooltip': 'Measure referent',
  'portfolio.col.rp': 'RP',
  'portfolio.col.rpTooltip': 'Participant referent',
  'portfolio.measuresRI': 'RI measures',
  'portfolio.measuresOAI': 'OAI measures',
  'portfolio.planProgressive': 'AI progression: {start}% → {target}% by {date}',
  'portfolio.planConstant': 'Constant AI rate {pct}%',
  'portfolio.exportExcel': 'Export Excel',
  'portfolio.exportExcelTooltip': 'Export the displayed rows (filters applied) to Excel',
  'portfolio.filter.typeAll': 'Measure type: all',
  'portfolio.filter.mandantAll': 'Mandant: all',
  'portfolio.filter.rmAll': 'RM: all',
  'portfolio.filter.rpAll': 'RP: all',
  'portfolio.filter.reset': 'Reset',
  'portfolio.export.presence30d': 'Presence 30 d (%)',
  'account.tab.account': 'Account',
  'account.tab.permissions': 'Permissions',
  'account.tab.notifications': 'Notifications',
  'account.pwd.errMinLength': 'New password must be at least 8 characters',
  'account.pwd.errMismatch': 'Passwords do not match',
  'account.pwd.errFailed': 'Failed to change password',
  'account.field.name': 'Name',
  'account.field.email': 'Email',
  'account.field.role': 'Role',
  'account.role.respAll': 'RESP. ALL',
  'account.role.respOai': 'RESP. OAI',
  'account.role.respRi': 'RESP. RI',
  'account.role.utilisateur': 'USER',
  'account.field.staffId': 'Staff ID',
  'account.field.auth': 'Auth',
  'account.auth.sso': 'Single Sign-On (OIDC)',
  'account.auth.password': 'Password',
  'account.section.changePassword': 'Change Password',
  'account.pwd.current': 'Current Password',
  'account.pwd.new': 'New Password',
  'account.pwd.minLengthHint': 'At least 8 characters',
  'account.pwd.confirm': 'Confirm New Password',
  'account.pwd.changed': 'Password Changed',
  'account.perm.capability': 'Capability',
  'account.perm.admin': 'Admin',
  'account.perm.respAll': 'Resp. all',
  'account.perm.respOai': 'Resp. OAI',
  'account.perm.respRi': 'Resp. RI',
  'account.perm.utilisateur': 'User',
  'account.perm.description': 'What your role ({role}) can do in the system.',
  'account.notif.title': 'Notification preferences',
  'account.notif.description': 'Choose which events you want to be notified about.',
  'account.notif.categoryOther': 'Other',
  'equipe.title': 'My team',
  'equipe.referentCount': '{n} referents',
  'equipe.participantCount': '{n} participants',
  'equipe.endingSoonCount': '{n} ending soon',
  'equipe.description': 'Overview of measure referents and their active participants. Colored bars flag situations to watch.',
  'equipe.noReferents': 'No referents with active participants.',
  'equipe.alert.endingSoon': '{n} measure(s) ending within the next 30 days',
  'equipe.alert.journalStale': 'Last journal entry {n} days ago on average',
  'equipe.metric.avgActivity': 'Average activity',
  'equipe.metric.avgActivityTooltip': 'Average hours per week over the last 4 weeks, across all participants',
  'equipe.metric.avgPresence': 'Average presence',
  'equipe.metric.avgPresenceTooltip': 'Average presence rate of all participants over the last 30 days',
  'equipe.metric.daysNoJournal': 'Days without journal',
  'equipe.metric.daysNoJournalTooltip': 'Average number of days since the last journal entry, per active measure',
  'equipe.metric.endingSoon': 'Ending soon',
  'equipe.metric.endingSoonTooltip': 'Number of measures ending within the next 30 days',
  'equipe.toggleHide': '▾ Hide participants',
  'equipe.toggleShow': '▸ Show participants',
  'equipe.col.participant': 'Participant',
  'equipe.col.contract': 'Contract',
  'equipe.col.mandant': 'Client',
  'equipe.col.daysRemaining': 'Days remaining',
  'equipe.col.hoursPerWeek': 'Hours / week',
  'conseillers.title': 'Advisors',
  'conseillers.count': '{n} advisors',
  'conseillers.activeMeasuresCount': '{n} active measures',
  'conseillers.totalCaPill': '{amount} CHF/month',
  'conseillers.totalLifetimePill': '{amount} CHF cumulative',
  'conseillers.description': 'Contracting contacts with participants in active measures. Click to see details.',
  'conseillers.searchPlaceholder': 'Search...',
  'conseillers.filter.allOrganizations': 'All organizations',
  'conseillers.sort.ca': 'Sort: Monthly revenue',
  'conseillers.sort.lifetime': 'Sort: Lifetime value',
  'conseillers.sort.active': 'Sort: Active measures',
  'conseillers.sort.total': 'Sort: Total measures',
  'conseillers.sort.name': 'Sort: Name',
  'conseillers.sort.organization': 'Sort: Organization',
  'conseillers.noneFound': 'No advisors found.',
  'conseillers.backToAll': '← All advisors',
  'conseillers.unknownOrganization': 'Unknown organization',
  'conseillers.since': 'Since {age} ({date})',
  'conseillers.card.activeCount': '{n} active',
  'conseillers.card.endingSoon': '{n} measure(s) ending within the next 30 days',
  'conseillers.card.rmPrefix': 'RM: {rm}',
  'conseillers.metric.monthlyRevenue': 'Monthly revenue',
  'conseillers.metric.monthlyRevenueTooltip': 'Sum of active measure prices',
  'conseillers.metric.lifetimeValue': 'Lifetime value',
  'conseillers.metric.lifetimeValueTooltip': 'Sum of price × months for all measures',
  'conseillers.metric.totalMeasures': 'Total measures',
  'conseillers.metric.completedTooltip': '{n} completed',
  'conseillers.metric.avgPresence': 'Avg. presence',
  'conseillers.metric.avgPresenceTooltip': 'Average presence rate of active participants (30d)',
  'conseillers.metric.relationship': 'Relationship',
  'conseillers.metric.firstMandateTooltip': 'First mandate: {date}',
  'conseillers.kpi.activity': 'Activity',
  'conseillers.kpi.activityTooltip': 'Actual hours of presence per week (average over the last 4 complete weeks, type "observed", status present).',
  'conseillers.unit.hoursPerWeek': 'h/wk',
  'conseillers.kpi.activityRate': 'Activity rate',
  'conseillers.kpi.activityRateTooltip': 'Occupancy percentage: actual hours per week ÷ 40h (full-time) × 100.',
  'conseillers.kpi.presence': 'Presence',
  'conseillers.kpi.presenceTooltip': 'Presence rate for the last 30 days: sessions present ÷ sessions observed × 100.',
  'conseillers.kpi.measure': 'Measure',
  'conseillers.kpi.remaining': 'remaining',
  'conseillers.unit.chfPerMonth': 'CHF/month',
  'conseillers.detail.caTooltip': 'Sum of monthly prices of active measures. Click to see history.',
  'conseillers.unit.chf': 'CHF',
  'conseillers.detail.cumulTooltip': 'Total sum of monthly revenue over the whole relationship. Click to see the chart.',
  'conseillers.detail.measuresLabel': 'Measures',
  'conseillers.detail.measuresTooltip': 'Number of active measures. Click to see the monthly trend.',
  'conseillers.unit.active': 'active',
  'conseillers.detail.measuresSub': '{total} total · {completed} completed',
  'conseillers.detail.endingSoonDelta': '{n} ending < 30d',
  'conseillers.detail.presenceTooltip': 'Average presence rate of all participants. Click to see the monthly history.',
  'conseillers.chart.title.ca': 'Monthly revenue — Revenue trend',
  'conseillers.chart.title.cumul': 'Lifetime value — Total revenue accumulated over time',
  'conseillers.chart.title.mesures': 'Active measures — Number of measures per month',
  'conseillers.chart.title.presence': 'Average presence — Monthly rate across all participants',
  'conseillers.chart.activeMeasuresLabel': '{n} measures',
  'conseillers.detail.activeParticipants': 'Active participants',
  'conseillers.detail.noActiveParticipants': 'No active participants.',
  'conseillers.chart.value': 'Value',
  'conseillers.chart.rollingAvg': '{n}-month avg',
  'conseillers.relative.months': '{n} months',
  'conseillers.relative.years': '{n} years',
  'conseillers.month.1': 'Jan',
  'conseillers.month.2': 'Feb',
  'conseillers.month.3': 'Mar',
  'conseillers.month.4': 'Apr',
  'conseillers.month.5': 'May',
  'conseillers.month.6': 'Jun',
  'conseillers.month.7': 'Jul',
  'conseillers.month.8': 'Aug',
  'conseillers.month.9': 'Sep',
  'conseillers.month.10': 'Oct',
  'conseillers.month.11': 'Nov',
  'conseillers.month.12': 'Dec',
  // ── from B.json ──
  'drilldown.tab.overview': 'Overview',
  'drilldown.tab.measures': 'Measures',
  'drilldown.tab.attendance': 'Attendance & Activities',
  'drilldown.tab.journal': 'Journal & Reports',
  'drilldown.tab.timeline': 'Timeline',
  'drilldown.tab.communications': 'Communications',
  'drilldown.notFound': 'Participant not found.',
  'drilldown.absence.short.vacation': 'Vac.',
  'drilldown.absence.short.illness': 'Sick',
  'drilldown.absence.short.accident': 'Acc.',
  'drilldown.absence.short.measure_suspended': 'Susp.',
  'drilldown.absence.short.default': 'Abs.',
  'drilldown.absence.full.vacation': 'Vacation',
  'drilldown.absence.full.illness': 'Illness',
  'drilldown.absence.full.accident': 'Accident',
  'drilldown.absence.full.measure_suspended': 'Measure suspended',
  'drilldown.absence.full.default': 'Absence',
  'drilldown.measureBanner.contract': 'Contract {n}',
  'drilldown.measureBanner.daysProgress': '{elapsed} d / {total}',
  'drilldown.measureBanner.none': 'No active measure.',
  'drilldown.kpi.activity.label': 'Actual activity',
  'drilldown.kpi.activity.info': 'Effective attendance hours for the last complete ISO week (Mon–Sun). Only real observations (recorded attendance, « observed » type) are counted. The current week is excluded as incomplete.\n\nDelta: difference from the average of the last 4 active weeks (weeks with at least one attendance).',
  'drilldown.kpi.hoursPerWeekSuffix': 'h / week',
  'drilldown.kpi.aiTargetTitleProgressive': 'AI target today (interpolated from {start}% to {target}%)',
  'drilldown.kpi.aiTargetTitleConstant': 'AI target {target}% constant',
  'drilldown.kpi.aiTargetHours': 'AI target: {hours} h ({pct}%)',
  'drilldown.kpi.deltaHours': '{n} h vs 4-wk avg',
  'drilldown.kpi.rate.label': 'Activity rate',
  'drilldown.kpi.rate.info': 'Occupancy percentage: effective hours for the last complete week divided by 40 h (full-time).\n\nThe sparkline shows the last 4 weeks. The delta compares the last week to the 4-week average.',
  'drilldown.kpi.rate.sub': 'of 40 h full-time',
  'drilldown.kpi.engagement.value': 'Realized/planned: {pct}%',
  'drilldown.kpi.lastWeekSub': 'Last week: {hours} h',
  'drilldown.kpi.rate.inline': 'i.e. {pct}% of full time',
  'drilldown.kpi.planVsReal.label': 'Planned vs Achieved (4 wks)',
  'drilldown.kpi.planVsReal.info': 'Orange dashed line = PLANNED, solid line = ACHIEVED (% of a 40h week; number under each point = achieved). Last completed week. PLANNED = planned hours (enrollments + unmarked), ACHIEVED = actually observed hours; both in hours and % of a 40h week. Orange band = planned, solid bar = achieved (green: plan met or exceeded). Number under each bar = achieved %.',
  'drilldown.kpi.planVsReal.plannedWord': 'Planned',
  'drilldown.kpi.planVsReal.hoursSub': 'last week: planned {planned_h} h · achieved {achieved_h} h',
  'drilldown.kpi.planVsReal.noPlan': 'no plan in the period',
  'drilldown.kpi.planVsReal.achievedWord': 'Achieved',
  'drilldown.kpi.planVsReal.avgSub': '4-wk mean — planned {planned}% · achieved {achieved}%',
  'drilldown.kpi.planVsReal.deltaVs': '{n} pts vs planned',
  'drilldown.kpi.planVsReal.weekTip': 'Planned {planned}% ({planned_h} h) · Achieved {achieved}% ({achieved_h} h)',
  'drilldown.kpi.lastWeekRateSub': 'Last week: {pct}%',
  'drilldown.kpi.engagement.info': 'Observed ÷ planned hours (last 4 completed weeks, planned = marked + unmarked). Unlike the activity rate (active weeks), absences weigh here.',
  'chart.legend.attended': 'Attended (marked present)',
  'chart.legend.missedExcused': 'Missed — excused',
  'chart.legend.missedUnexcused': 'Missed — unexcused',
  'chart.legend.unmarked': 'Unmarked',
  'drilldown.kpi.aiTargetPct': 'AI target: {pct}%',
  'drilldown.kpi.deltaPct': '{n}% vs 4-wk avg',
  'drilldown.kpi.presence.label': 'Attendance',
  'drilldown.kpi.presence.info': 'Attendance rate for the last complete week: sessions attended ÷ sessions observed × 100.\n\nThe sparkline shows the last 4 weeks. The delta compares the last week to the 4-week average.',
  'drilldown.kpi.presence.sub': 'last week',
  'drilldown.kpi.absences.label': 'Absences',
  'drilldown.kpi.absences.info': 'Absences counted since joining L’Éveil ({months} months).\n\n• Illness: full days with no attendance at all, excused with « Illness » reason. Entitlement: 1 day per month at L’Éveil ({allowed} d allowed).\n\n• Missed workshops: excused sessions on days when the participant was otherwise present. Tolerance: 3 sessions.\n\n• Unexcused: absences with no justification. No tolerance.',
  'drilldown.kpi.absences.sick': 'Illness',
  'drilldown.kpi.absences.workshops': 'Workshops',
  'drilldown.kpi.absences.unexcused': 'Unexcused',
  'drilldown.kpi.absences.remaining': '{remaining} left / {total}',
  'drilldown.unit.daysAbbrev': '{n} d',
  'drilldown.chart.title.activity': 'Actual activity — Hours per week',
  'drilldown.chart.title.rate': 'Activity rate — % of 40 h full-time',
  'drilldown.chart.title.presence': 'Attendance — Weekly rate',
  'drilldown.chart.plannedHours': 'Planned (h)',
  'drilldown.chart.plannedPct': 'Planned (%)',
  'drilldown.chart.aiTargetProgressive': 'AI target {start}→{target}%',
  'drilldown.chart.aiTargetConstant': 'AI target {target}%',
  'drilldown.appointments.upcoming': 'Upcoming appointments',
  'drilldown.appointments.totalCount': '({list} total)',
  'drilldown.contacts.title': 'Key contacts',
  'drilldown.contacts.advisor': 'Advisor',
  'drilldown.objectives.title': 'Objectives',
  'drilldown.objectives.bspSheet': 'BSP sheet',
  'drilldown.objectives.reportWithKind': 'Report ({kind})',
  'drilldown.objectives.report': 'Report',
  'drilldown.objectives.empty': 'No objectives extracted from documents.',
  'drilldown.objectives.activityRate': 'Activity rate',
  'drilldown.objectives.adjusted': 'Adjusted',
  'drilldown.objectives.adjustAction': '✏ Adjust',
  'drilldown.objectives.adjustActionTitle': 'Adjust the activity rate for this measure',
  'drilldown.objectives.progression': 'Progression',
  'drilldown.objectives.progressionRange': '{start}% → {target}%',
  'drilldown.objectives.progressionConstant': '{target}% (constant)',
  'drilldown.objectives.period': 'Period',
  'drilldown.objectives.source': 'Source',
  'drilldown.objectives.rmAdjustment': 'RM adjustment',
  'drilldown.objectives.aiMandate': 'AI mandate',
  'drilldown.objectives.qualitativeObjectives': 'Qualitative objectives',
  'drilldown.objectives.noQuantTarget': 'No quantifiable target',
  'drilldown.objectives.programme': 'Programme',
  'drilldown.objectives.type': 'Type',
  'drilldown.objectives.constant': 'Constant',
  'drilldown.objectives.startPct': 'start {pct}%',
  'drilldown.objectives.mention': 'Mention',
  'drilldown.objectives.participantReport': 'Participant report',
  'drilldown.objectives.participantWords': 'Participant\'s words',
  'drilldown.objectives.reportExcerpt': 'Report excerpt',
  'drilldown.objectives.viewPdf': 'View PDF',
  'drilldown.objectives.personalObjectives': 'Personal objectives',
  'drilldown.objectives.means': 'Means',
  'drilldown.objectives.recommendedActions': 'Recommended actions',
  'drilldown.objectives.motivations': 'Motivations',
  'drilldown.evaluations.title': 'Evaluations',
  'drilldown.personalInfo.title': 'Personal information',
  'drilldown.personalInfo.birth': 'Born',
  'drilldown.personalInfo.email': 'Email',
  'drilldown.personalInfo.phone': 'Phone',
  'drilldown.personalInfo.address': 'Address',
  'drilldown.bsp.title': 'Situation report',
  'drilldown.bsp.date': 'Date',
  'drilldown.bsp.location': 'Location',
  'drilldown.bsp.charterSigned': 'Charter signed',
  'drilldown.bsp.referent': 'Referent',
  'drilldown.bsp.formation': 'Training',
  'drilldown.bsp.health': 'Health',
  'drilldown.bsp.psychological': 'Psychological',
  'drilldown.bsp.family': 'Family',
  'drilldown.bsp.medication': 'Medication',
  'drilldown.bsp.hobbies': 'Interests',
  'drilldown.bsp.availability': 'Availability',
  'drilldown.bsp.notes': 'Notes',
  'drilldown.bsp.bspObjectives': 'BSP objectives',
  'drilldown.bspDetail.title': 'First interview sheet (BSP)',
  'drilldown.bspDetail.viewSource': 'View source document',
  'drilldown.bspDetail.strengths': 'Strengths and resources',
  'drilldown.bspDetail.limitations': 'Limitations',
  'drilldown.bspDetail.professionalDirection': 'Professional direction',
  'drilldown.bspDetail.alternativePaths': 'Alternative paths',
  'drilldown.bspDetail.projectIdeas': 'Professional project ideas',
  'drilldown.bspDetail.desiredRate': 'Desired activity rate',
  'drilldown.bspDetail.lastProfession': 'Last profession',
  'drilldown.bspDetail.trainings': 'Training(s)',
  'drilldown.bspDetail.languagesIt': 'Languages / IT skills',
  'drilldown.bspDetail.availabilities': 'Availabilities',
  'drilldown.documents.title': 'Documents',
  'drilldown.documents.attributedCount': '{n} attributed',
  'drilldown.documents.unattributedCount': '{n} unattributed',
  'drilldown.documents.files': 'Files',
  'drilldown.documents.mandateTitle': 'Mandate / OAI decision',
  'drilldown.documents.mandate': 'Mandate',
  'drilldown.documents.intake': 'Intake',
  'drilldown.documents.sizeKo': '{n} KB',
  'drilldown.measuresTab.none': 'No measure found.',
  'drilldown.measuresTab.current': 'Ongoing',
  'drilldown.measuresTab.contractCostTitle': 'Cost extracted from contract{n}{type}{months}',
  'drilldown.measuresTab.contractCost': 'Contract: CHF {amount}',
  'drilldown.measuresTab.catalogPriceTitle': 'Catalog price (subtype_price)',
  'drilldown.measuresTab.catalogPrice': 'Catalog: CHF {amount}',
  'drilldown.measuresTab.monthsPlanned': '{n} months planned',
  'drilldown.measuresTab.article': 'Art. {n}',
  'drilldown.measuresTab.daysRemaining': '{n} d left',
  'drilldown.measuresTab.mandant': 'Mandant:',
  'drilldown.measuresTab.advisor': 'Advisor:',
  'drilldown.measuresTab.contract': 'Contract:',
  'drilldown.measuresTab.presencePct': 'Attendance: {pct}%',
  'drilldown.measuresTab.perWeek': '{hours} / wk',
  'drilldown.measuresTab.totalHours': '{hours} total',
  'drilldown.measuresTab.sessionsPerWeek': '{sessions} sessions / {weeks} wk',
  'drilldown.measuresTab.vacationDays': '{n} d vacation',
  'drilldown.measuresTab.journalCount': '{n} journal',
  'drilldown.measuresTab.reportCount': '{n} reports',
  'drilldown.measuresTab.objectives': 'Objectives:',
  'drilldown.measuresTab.rateRange': 'Rate {start}% → {target}%',
  'drilldown.measuresTab.rateConstant': 'Rate {target}% constant',
  'drilldown.measuresTab.byDate': 'by {date}',
  'drilldown.measuresTab.sourcePdf': 'source PDF',
  'drilldown.measuresTab.workshops': 'Workshops:',
  'drilldown.measuresTab.workshopStat': '{activity}: {sessions} s. · {hours}',
  'drilldown.measuresTab.documentsCount': 'Documents ({n}):',
  'drilldown.dow.mon': 'Mon',
  'drilldown.dow.tue': 'Tue',
  'drilldown.dow.wed': 'Wed',
  'drilldown.dow.thu': 'Thu',
  'drilldown.dow.fri': 'Fri',
  'drilldown.dow.sat': 'Sat',
  'drilldown.dow.sun': 'Sun',
  'drilldown.attendance.aiTitle': 'Attendance analysis',
  'drilldown.attendance.aiButton': 'Analyze',
  'drilldown.attendance.aiPeriodHint': 'Without a selected period, the analysis covers the last 12 months of attendance.',
  'drilldown.attendance.heatmapTitle': 'Attendance calendar (last 12 months)',
  'drilldown.attendance.appointments': 'Appointments',
  'drilldown.attendance.colDate': 'Date',
  'drilldown.attendance.colType': 'Type',
  'drilldown.attendance.colSchedule': 'Schedule',
  'drilldown.attendance.colStaff': 'Staff',
  'drilldown.attendance.colComment': 'Comment',
  'drilldown.attendance.viewDetail': 'View detail',
  'drilldown.attendance.plannedWorkshops': 'Planned workshops',
  'drilldown.attendance.currentMeasure': 'current measure',
  'drilldown.attendance.sessionsPerWeek': '{n} session{s} / wk',
  'drilldown.attendance.colActivity': 'Activity',
  'drilldown.attendance.colSession': 'Session',
  'drilldown.attendance.colDay': 'Day',
  'drilldown.attendance.colPeriod': 'Period',
  'drilldown.attendance.ongoing': 'ongoing',
  'drilldown.attendance.otherCategory': 'Other',
  'drilldown.attendance.workshopsAttended': 'Workshops attended',
  'drilldown.attendance.presenceStat': 'Attendance: {value}',
  'drilldown.attendance.daysCount': '{n} days',
  'drilldown.attendance.currentMeasureCol': 'Current measure',
  'drilldown.attendance.allMeasuresCol': 'All measures',
  'drilldown.attendance.colSessions': 'Sessions',
  'drilldown.attendance.colHours': 'Hours',
  'drilldown.attendance.total': 'Total',
  'drilldown.attendance.externalPlacements': 'External placements',
  'drilldown.attendance.daysTotal': '{n} days total',
  'drilldown.attendance.daysLast3mo': '{n} days (last 3 months)',
  'drilldown.attendance.monthlySummary': 'Monthly summary',
  'drilldown.attendance.colMonth': 'Month',
  'drilldown.attendance.colWorkshop': 'Workshop',
  'drilldown.attendance.colAttendances': 'Attendances',
  'drilldown.attendance.previousMeasures': '↳ Previous measures',
  'drilldown.attendance.formalAbsences': 'Formal absences',
  'drilldown.attendance.noAbsences': 'No absence recorded.',
  'drilldown.attendance.colStart': 'Start',
  'drilldown.attendance.colEnd': 'End',
  'drilldown.journal.period': 'Period',
  'drilldown.journal.from': 'From',
  'drilldown.journal.to': 'To',
  'drilldown.journal.aiPeriodHint': 'Analyzed period: {from} → {to}',
  'drilldown.journal.aiPeriodDefault': 'Without a selected period, the synthesis covers the last 6 months.',
  'drilldown.journal.clear': '✕ Clear',
  'drilldown.journal.entriesCount': '{n} / {total} entries',
  'drilldown.journal.aiSynthesisTitle': 'Journal synthesis',
  'drilldown.journal.aiSynthesisButton': 'Generate',
  'drilldown.journal.title': 'Journal',
  'drilldown.journal.entryCount': '{n} entries',
  'drilldown.journal.emptyAll': 'No journal entries.',
  'drilldown.journal.emptyPeriod': 'No entries in this period.',
  'drilldown.journal.entryTypeFallback': 'Type {type}',
  'drilldown.journal.attendanceCommentTitle': 'General comment entered on an attendance record (animator/RP)',
  'drilldown.journal.attendanceBadge': 'Attendance',
  'drilldown.journal.byAuthor': 'by {name}',
  'drilldown.journal.reportsTitle': 'Assessment reports',
  'drilldown.journal.reportsCount': '{n} reports',
  'drilldown.journal.noReports': 'No assessment report.',
  'drilldown.journal.charCount': '{n} chars',
  'drilldown.journal.nextSteps': 'Next steps:',
  'drilldown.journal.noContent': 'No content.',
  'drilldown.timeline.icon.attendance': 'Attendance',
  'drilldown.timeline.icon.firstWorkshop': 'New workshop',
  'drilldown.timeline.icon.journal': 'Journal',
  'drilldown.timeline.icon.absence': 'Absence',
  'drilldown.timeline.icon.vacation': 'Vacation',
  'drilldown.timeline.icon.measureStart': 'Measure start',
  'drilldown.timeline.icon.measureEnd': 'Measure end',
  'drilldown.timeline.icon.report': 'Report',
  'drilldown.timeline.icon.placement': 'External placement',
  'drilldown.timeline.icon.appointment': 'Appointment',
  'drilldown.timeline.icon.appointmentFuture': 'Upcoming appointment',
  'drilldown.timeline.weekRange': '{start} – {end} {month} {year}',
  'drilldown.timeline.title': 'Timeline',
  'drilldown.timeline.eventCount': '{n} events',
  'drilldown.timeline.noEvents': 'No events.',
  'drilldown.timeline.weekOf': 'Week of {range}',
  'drilldown.timeline.firstTime': '★ First {list}',
  'drilldown.measuresTab.monthsCount': '{n} months',
  'drilldown.personalInfo.ageYears': '{n} years',
  // ── from C.json ──
  'comms.pageTitle': 'Communications',
  'comms.unsentTooltip': 'Drafts to send',
  'comms.unsentCount': '{n} drafts',
  'comms.sentTooltip': 'Sent communications',
  'comms.sentCount': '{n} sent',
  'comms.tabUnsent': 'Drafts',
  'comms.tabSent': 'Sent',
  'comms.tabAll': 'All',
  'comms.searchPlaceholder': 'Search (participant, recipient, subject…)',
  'comms.errLoad': 'Loading error',
  'comms.errSave': 'Error while saving',
  'comms.errMarkSent': 'Error while updating',
  'comms.errDelete': 'Error while deleting',
  'comms.emptyUnsent': 'No drafts to send.',
  'comms.emptySent': 'No sent communications.',
  'comms.emptyAll': 'No communications.',
  'comms.colParticipant': 'Participant',
  'comms.colMeasure': 'Measure',
  'comms.colType': 'Type',
  'comms.colRecipient': 'Recipient',
  'comms.colGenerated': 'Generated',
  'comms.colStatus': 'Status',
  'comms.kindAdjustment': 'Rate adjustment',
  'comms.generatedBy': 'by {name}',
  'comms.generatedOn': 'Generated {date}',
  'comms.sentBadge': '✓ Sent · {date}',
  'comms.sentBadgeLong': '✓ Sent on {date}',
  'comms.markedByTooltip': 'Marked by {name}',
  'comms.draftBadge': '● Draft',
  'comms.subjectLabel': 'Subject:',
  'comms.copySubjectPrefix': 'Subject',
  'comms.copied': 'Copied!',
  'comms.copyButton': 'Copy (subject + body)',
  'comms.openInMail': 'Open in mail',
  'comms.openInMailTooltip': 'Open in the default mail client',
  'comms.markSent': 'Mark as sent',
  'comms.openParticipant': 'Open participant ↗',
  'comms.confirmMarkSentTitle': 'Mark as sent?',
  'comms.confirmDeleteTitle': 'Delete this draft?',
  'comms.confirmMarkSentMsg': 'This action is final: the status will switch to "Sent" with today\'s date. You won\'t be able to revert to "Draft".',
  'comms.confirmDeleteMsg': 'The draft will be permanently deleted and removed from the list.',
  'comms.confirmMarkSentLabel': 'Mark sent',
  'comms.emptyParticipant': 'No communication generated for this participant.',
  'comms.emptyParticipantHint': 'Emails created during a rate adjustment appear here, ready to be copied into Outlook.',
  'comms.toLabel': 'To:',
  'comms.relatedAdjustment': 'Adjustment #{id}',
  'notifs.minutesAgo': '{n} min',
  'notifs.hoursAgo': '{n}h ago',
  'notifs.daysAgo': '{n}d ago',
  'notifs.kindJournalStale': 'Journal without entry',
  'notifs.reasonJournalAdded': 'Entry added',
  'notifs.reasonMesureEnded': 'Measure ended',
  'notifs.reasonMutedApproved': 'Mute approved',
  'notifs.pageTitle': 'Notifications',
  'notifs.activeTooltip': 'Active alerts',
  'notifs.activeCount': '{n} active',
  'notifs.archivedTooltip': 'Archived alerts',
  'notifs.archivedCount': '{n} archived',
  'notifs.tabOpen': 'Active',
  'notifs.tabPast': 'Archived',
  'notifs.tabMuteRequests': 'Mute requests',
  'notifs.errLoad': 'Loading error',
  'notifs.errMuteRequest': 'Error while requesting mute',
  'notifs.errDecision': 'Error while submitting decision',
  'notifs.mutePromptTitle': 'Request a mute',
  'notifs.mutePromptBody': 'This request will be sent to the manager for approval. If accepted, this alert will no longer appear for the measure "{measure}" of ',
  'notifs.reasonRequiredLabel': 'Reason (required)',
  'notifs.reasonPlaceholder': 'Explain why this participant is exempt from daily follow-up…',
  'notifs.sendRequest': 'Send request',
  'notifs.approveTitle': 'Approve the mute?',
  'notifs.denyTitle': 'Deny the request?',
  'notifs.approveMsg': 'The "{kind}" alert will no longer appear for the measure "{measure}" of {name}.',
  'notifs.denyMsg': 'The mute request will be denied. {name} will be notified.',
  'notifs.defaultRequester': 'The requester',
  'notifs.approveLabel': 'Approve',
  'notifs.denyLabel': 'Deny',
  'notifs.emptyPast': 'No archived alerts.',
  'notifs.emptyOpen': 'No active alerts. 🎉',
  'notifs.colType': 'Type',
  'notifs.colParticipant': 'Participant',
  'notifs.colMeasure': 'Measure',
  'notifs.colDetail': 'Detail',
  'notifs.colClosed': 'Closed',
  'notifs.colOpenSince': 'Open since',
  'notifs.daysSinceNoJournal': '{n} days without journal entry',
  'notifs.lastEntryDate': '(last: {date})',
  'notifs.muteTooltip': 'Request a mute for this measure',
  'notifs.muteButton': 'Mute',
  'notifs.emptyMuteRequests': 'No mute requests.',
  'notifs.colStatus': 'Status',
  'notifs.colRequester': 'Requester',
  'notifs.colReason': 'Reason',
  'notifs.colRequested': 'Requested',
  'notifs.statusPending': 'Pending',
  'notifs.statusApproved': 'Approved',
  'notifs.statusDenied': 'Denied',
  'notifs.noneParenthesized': '(none)',
  'notifs.decidedBy': 'by {name}',
  'adjust.historyToggle': 'Plan history ({n})',
  'adjust.errLoad': 'Loading error',
  'adjust.errDelete': 'Error while deleting',
  'adjust.badgeMandat': 'MANDATE',
  'adjust.mandatAiLabel': 'AI mandate',
  'adjust.inEffect': '· in effect',
  'adjust.periodLabel': 'Period: {start} – {end}',
  'adjust.effectLine': 'Effective {date} · {created}',
  'adjust.superseded': '· superseded',
  'adjust.reasonLabel': 'Reason:',
  'adjust.deleteTooltip': 'Delete this adjustment',
  'adjust.confirmDeleteTitle': 'Delete this adjustment?',
  'adjust.confirmDeleteCurrentMsg': 'The active adjustment will be deleted. The previous adjustment (if any) will become active again, otherwise the initial AI mandate will take back control. The associated email draft will also be deleted.',
  'adjust.confirmDeleteOldMsg': 'The adjustment will be removed from the history. The associated email draft will also be deleted. The active chain is not affected.',
  'adjust.planRange': '{start}% → {target}%',
  'adjust.planUntilSuffix': ' by {date}',
  'adjust.planConstant': 'Constant at {target}%',
  'adjust.modalTitle': 'Adjust the activity rate',
  'adjust.genericMeasure': 'measure',
  'adjust.closeTooltip': 'Close (Esc)',
  'adjust.currentPlanLabel': 'Current plan',
  'adjust.labelType': 'Type',
  'adjust.typeProgressive': 'Progressive',
  'adjust.typeConstant': 'Constant',
  'adjust.labelRate': 'Rate',
  'adjust.labelDeadline': 'Deadline',
  'adjust.labelSource': 'Source',
  'adjust.sourceAdjustmentRm': 'RM adjustment',
  'adjust.pdfLink': 'PDF ↗',
  'adjust.objectivesLabel': 'Objectives:',
  'adjust.noPlanExtracted': 'No AI plan extracted for this measure.',
  'adjust.newPlanLabel': 'New plan',
  'adjust.labelStartRate': 'Start rate',
  'adjust.labelTargetRate': 'Target rate',
  'adjust.labelPeriodStart': 'Period start',
  'adjust.labelEffectiveDate': 'Effective date',
  'adjust.mondaySnapHint': '↦ Effective date and period start are snapped to the Monday of the chosen week (weeks are never split in two).',
  'adjust.qualitativeObjectivesLabel': 'Qualitative objectives (one per line, optional)',
  'adjust.qualitativeObjectivesPlaceholder': 'E.g.: Stabilize attendance&#10;Reach 60% of schedule by June',
  'adjust.reasonRequiredLabel': 'Reason for the adjustment',
  'adjust.reasonPlaceholder': 'Explain why the current plan is no longer sustainable and what the adjustment aims to achieve.',
  'adjust.reasonMinLength': 'Minimum 5 characters.',
  'adjust.oaiNoteLabel': 'Additional note for the OAI advisor (optional)',
  'adjust.recipientNameLabel': 'Recipient — name',
  'adjust.recipientNamePlaceholder': 'OAI advisor',
  'adjust.recipientEmailLabel': 'Recipient — email',
  'adjust.recipientEmailPlaceholder': 'conseiller@oai.ch',
  'adjust.previewToggle': 'Preview of the email to generate',
  'adjust.subjectLabel': 'Subject:',
  'adjust.submitButton': 'Save and generate',
  'adjust.errSave': 'Error while saving',
  // ── from D.json ──
  'chart.noData': 'No data',
  'chart.month.jan': 'Jan',
  'chart.month.feb': 'Feb',
  'chart.month.mar': 'Mar',
  'chart.month.apr': 'Apr',
  'chart.month.may': 'May',
  'chart.month.jun': 'Jun',
  'chart.month.jul': 'Jul',
  'chart.month.aug': 'Aug',
  'chart.month.sep': 'Sep',
  'chart.month.oct': 'Oct',
  'chart.month.nov': 'Nov',
  'chart.month.dec': 'Dec',
  'chart.weekday.mon': 'Mon',
  'chart.weekday.tue': 'Tue',
  'chart.weekday.wed': 'Wed',
  'chart.weekday.thu': 'Thu',
  'chart.weekday.fri': 'Fri',
  'chart.weekdayShort.sun': 'Sun',
  'chart.weekdayShort.mon': 'Mon',
  'chart.weekdayShort.tue': 'Tue',
  'chart.weekdayShort.wed': 'Wed',
  'chart.weekdayShort.thu': 'Thu',
  'chart.weekdayShort.fri': 'Fri',
  'chart.weekdayShort.sat': 'Sat',
  'chart.legend.exceedTitle': 'Current above target',
  'chart.legend.exceed': 'Above',
  'chart.legend.reachedTitle': 'Current at target (≥ 95%)',
  'chart.legend.reached': 'Reached',
  'chart.legend.closeTitle': 'Current close to target (80–95%)',
  'chart.legend.close': 'Close',
  'chart.legend.belowTitle': 'Current below 80% of target',
  'chart.legend.below': 'Below',
  'chart.legend.targetTitle': 'Target level prescribed for the week',
  'chart.legend.target': 'Target',
  'chart.legend.priorTitle': 'Participant\'s prior measures (muted palette)',
  'chart.legend.prior': 'Prior measures',
  'chart.legend.plannedTitle': 'Planned hours/attendance (active enrollments)',
  'chart.legend.plannedDefault': 'Planned',
  'chart.legend.noAttendance': 'no attendance',
  'chart.tooltip.week': 'Week {n}',
  'chart.tooltip.priorMeasurePrefix': 'Prior measure: ',
  'chart.tooltip.estimated': 'Estimated',
  'chart.tooltip.actual': 'Actual',
  'chart.tooltip.planned': 'Planned',
  'chart.tooltip.plannedUpcoming': 'Planned (upcoming)',
  'chart.tooltip.targetAi': 'AI target',
  'chart.tooltip.delta': 'Delta',
  'chart.tooltip.rollingAvg': 'Avg. {n} wk',
  'chart.absence.vacation': 'Vacation',
  'chart.absence.illness': 'Illness',
  'chart.absence.accident': 'Accident',
  'chart.absence.measureSuspended': 'Measure suspended',
  'chart.absence.default': 'Absence',
  'timeline.measure.title': 'Art. 14 pathway',
  'timeline.measure.priorMonths': '{n} prior months (other institution)',
  'timeline.measure.budgetLabel': 'Month {month} / {budget}',
  'timeline.milestone.taux30': '30%',
  'timeline.milestone.taux50': '50%',
  'timeline.milestone.tauxMin': 'min. rate',
  'timeline.milestone.etActif': 'ET active',
  'timeline.milestone.auPlusTard': 'at the latest',
  'timeline.milestone.stageExt': 'Ext. internship',
  'timeline.milestone.requis': 'required',
  'timeline.milestone.tooltipBeforeEveil': 'Milestone already reached before L\'Éveil',
  'timeline.milestone.tooltipPastOk': 'Milestone reached ✓',
  'timeline.milestone.tooltipPastWarn': 'Milestone not reached — action required',
  'timeline.milestone.tooltipApproaching': 'Due in less than 45 days',
  'timeline.milestone.tooltipFuture': 'Upcoming milestone',
  'timeline.contract.label': 'Contract {n}',
  'timeline.phase.transition': 'EP → ET',
  'timeline.phase.planned': '(planned)',
  'timeline.legend.epFull': 'Progressive training (EP)',
  'timeline.legend.etFull': 'Work training (ET)',
  'timeline.legend.priorInstitution': 'Prior period (other institution)',
  'timeline.legend.today': 'Today',
  'timeline.ri.title': 'RI pathway',
  'timeline.ri.tauxActivite': 'Activity rate:',
  'timeline.ri.cycle': 'Cycle {n}',
  'timeline.ri.done': 'Completed',
  'timeline.ri.contract1Label': 'Contract 1 — CCC agreement',
  'timeline.ri.contract2Label': 'Contract 2 — CCC agreement',
  'timeline.ri.contractEndsIn': 'Contract ends in {n} d',
  'timeline.ri.pauseActive': '⏸ Pause: {n} d',
  'timeline.ri.pauseDuration': 'Pause: {n} d',
  'timeline.ri.legendContract1': 'Contract 1',
  'timeline.ri.legendContract2': 'Contract 2',
  'ai.generate': 'Generate',
  'ai.from': 'From',
  'ai.to': 'To',
  'ai.generationFailed': 'Generation failed',
  'appointment.noComment': 'No comment recorded.',
  'doc.confidence.high': 'high-confidence match',
  'doc.confidence.medium': 'likely match',
  'doc.confidence.none': 'unattributed',
  'doc.status.ocrFailed': 'OCR failed',
  'doc.status.ocrProcessing': 'OCR in progress',
  'doc.previewAlt': 'Page 1 preview — {name}',
  'doc.page': 'page',
  'doc.pages': 'pages',
  'doc.download': 'Download',
  'doc.summary': 'Summary',
  'doc.detectedMarkers': 'Detected markers',
  'doc.ocrText': 'OCR text ({n} characters)',
  'doc.ingestError': 'Ingestion error',
};

export const dictionaries: Record<Lang, Dict> = { fr, en };
